import { parseHealDelaysMs } from './utils/functions/healDelays'
import { DEFAULT_PRODUCT_PENDING_POLL_MS } from './utils/functions/productPendingPoll'
import { DEFAULT_CART_PRICE_CHECK_POLL_MS } from './utils/functions/cartPriceCheck'
import {
  parseListSearchDebounceMs,
  parseListSearchMinChars,
} from './utils/functions/listSearchThrottle'
import { parseUploadClientTimeoutMs } from './utils/functions/uploadClientTimeout'
import { parseSupplierStatusPollMs } from './utils/functions/supplierStatusPoll'
import { parsePdfSharpWaitMs } from './utils/functions/pdfSharpGate'

export const enviroment = {
  // Brand-laget (rebranding Jet Sport -> ID Unlimited, paabegyndt 26/8):
  // ALLE standard-brandvaerdier samlet ét sted og overstyrbare pr. miljoe.
  // Uaendrede defaults = Jet Sport, saa denne aendring i sig selv aendrer
  // INTET synligt - men selve skiftet bliver en yml-indstilling
  // (REACT_APP_BRAND_*), ikke en kodeaendring. Vendor-whitelabels overstyrer
  // fortsat alt dette pr. organisation via WhiteLabelContext.
  brandName: process.env['REACT_APP_BRAND_NAME'] || 'Jet Sport',
  brandWebsite: process.env['REACT_APP_BRAND_WEBSITE'] || 'jetsport.dk',
  brandEmail: process.env['REACT_APP_BRAND_EMAIL'] || 'post@jetsport.dk',
  brandPhone: process.env['REACT_APP_BRAND_PHONE'] || '+45 86 61 12 55',
  apiUrl: process.env['REACT_APP_API']!,
  authority: process.env['REACT_APP_AUTHORITY']!,
  scopePrefix: process.env['REACT_APP_SCOPE_PREFIX']!,
  clientId: process.env['REACT_APP_CLIENT_ID']!,
  authorityDomain: process.env['REACT_APP_AUTHORITY_DOMAIN']!,
  externalApiUrl: process.env['REACT_APP_EXTERNAL_API_URL'] ?? '',
  // Proof-PDF function key (pakke 1, 27/9): the FUNCTION-scope key
  // «proofpdf» that opens ONLY the two js-upload for-pdf routes
  // (GetLogosForPdfProxy + GetDesignsForPdfProxy) on the internal API. It is
  // a build secret, NOT a flag: CI gets it from the GitHub Actions repo
  // secrets PROOF_PDF_FUNCTION_KEY_DEV / PROOF_PDF_FUNCTION_KEY_PROD (one per
  // workflow yml), local builds from .env.local (never from the tracked .env
  // files). Dev and prod have their own value. Empty (unset) = the public proof-PDF page shows a
  // "not configured" message and never calls the API. Replaced the former
  // HOST-level js-upload key that lived in git and opened every
  // Function-level route.
  proofPdfFunctionKey: process.env['REACT_APP_PROOF_PDF_FUNCTION_KEY'] ?? '',
  apiKeysAdminEmails: (process.env['REACT_APP_API_KEYS_ADMIN_EMAILS'] ?? '')
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean),
  // Build id of the deployed bundle - a build value, not a feature flag: CI
  // sets REACT_APP_BUILD_ID to the commit sha in BOTH workflow ymls (dev and
  // prod). It rides on the /locales/{lng}/translation.json URL as ?v=<sha>
  // (src/i18n.ts) so every deploy fetches its own translation files. Unset
  // (local builds) = the backend's default path, exactly today's request.
  buildId: process.env['REACT_APP_BUILD_ID'] ?? '',
  // Feature flag: when "true", the nav-bar pending-orders badge fetch (getAllOrders, pageSize=1000)
  // is gated to Vendors only. Admins/customers never see that badge, so they skip the wasteful call.
  // Default (unset/"false") = current behavior (always fetch). Set per-env in .env.dev / .env.prod.
  gateOrdersBadge: process.env['REACT_APP_GATE_ORDERS_BADGE'] === 'true',
  // Feature flag: admin "act as vendor" — pick a vendor + one of its users, browse as them
  // and place orders on their behalf (API: CommitOrderDto.OnBehalfOfUserId, admin-only).
  // Default (unset/"false") = feature invisible. CI builds don't read .env.dev — the flag
  // must be set in the workflow yml env block to reach a deployed bundle.
  actAsVendorEnabled: process.env['REACT_APP_ACT_AS_VENDOR_ENABLED'] === 'true',
  // Feature flag: logo-sharing admin UI (Etape 3) — sharing relations /
  // chain overview / global-exclusion panels on the organization details
  // screen plus a small "Sharing" section on the logo detail popup. The
  // backing API (Etape 2) ships separately; until it is live every sharing
  // endpoint answers 404 and the UI shows a friendly "sharing API not active
  // yet" state instead of crashing. Default (unset/"false") = feature
  // invisible. CI builds don't read .env.dev — the flag must be set in the
  // workflow yml env block to reach a deployed bundle.
  logoSharingEnabled: process.env['REACT_APP_LOGO_SHARING_ENABLED'] === 'true',
  // Feature flag: prepayment-required prompt in the place-order flow. When
  // "true", confirming an order first runs a lightweight prepayment pre-check
  // and — only if the API says prepayment is required — pops a confirmation
  // dialog before placing. Default (unset/"false") = the exact current
  // behavior: no pre-check, no dialog, order placed straight away (the
  // OrderPlaced screen still diverts to Stripe as today). CI builds don't read
  // .env.dev — the flag must be set in the workflow yml env block to reach a
  // deployed bundle.
  prepaymentPromptEnabled:
    process.env['REACT_APP_PREPAYMENT_PROMPT_ENABLED'] === 'true',
  // Feature flag: admin "billing lookup" pages — the Billing overview (which
  // organizations have a billing vendor attached, i.e. the invoicing
  // hierarchy) plus the companion Vendor users lookup (search a person and see
  // which vendor they belong to). Both read new admin-only paged endpoints
  // (organizations/billing-overview and users/vendor-users). Until those are
  // live every call answers 404/500 and the screens show a friendly
  // "not active yet" state instead of crashing. Default (unset/"false") =
  // the two nav links are invisible. CI builds don't read .env.dev — the flag
  // must be set in the workflow yml env block to reach a deployed bundle.
  billingOverviewEnabled:
    process.env['REACT_APP_BILLING_OVERVIEW_ENABLED'] === 'true',
  // Feature flag: "Address line 2" on the checkout delivery form. Paired with
  // the API flag OrderShipToAddress2Enabled (CommitOrderDto.ShipToAddress2 →
  // NAV shipToParty.address2): only enable here where the API flag is ON —
  // with the API flag off the value is silently discarded, and the customer
  // must never type something that vanishes. Default (unset/"false") = field
  // invisible and the key omitted from the payload (wire-identical to before).
  // CI builds don't read .env.dev — the flag must be set in the workflow yml
  // env block to reach a deployed bundle.
  shipToAddress2Enabled:
    process.env['REACT_APP_SHIPTO_ADDRESS2_ENABLED'] === 'true',
  // Feature flag: admin-only "Cancel order" action on Awaiting payment rows in
  // the order list. Paired with the API flag AdminOrderCancelEnabled (the
  // endpoint answers 404 while that is off) - only enable here where the API
  // flag is ON. Default (unset/"false") = no button, UI identical to before.
  // CI builds don't read .env.dev - the flag must be set in the workflow yml
  // env block to reach a deployed bundle.
  orderCancelEnabled:
    process.env['REACT_APP_ORDER_CANCEL_ENABLED'] === 'true',
  // Feature flag: admin-only "Adjust prices" on Awaiting payment rows in the
  // order list (Nichlas 15/9: customers with agreed special prices get a
  // Stripe link on our standard prices). Opens a dialog with "Our price" /
  // "Agreed price" per line, recomputed totals, and on save the API issues a
  // new payment link + mail and expires the old one. Also shows the agreed
  // price + "Prices adjusted by" banner on the admin order details. Paired
  // with the API flag AdminPriceAdjustmentEnabled (the price-adjustment
  // routes answer 404 while that is off) - only enable here where the API
  // flag is ON. Default (unset/"false") = no button, no columns, UI
  // identical to before. CI builds don't read .env.dev - the flag must be
  // set in the workflow yml env block to reach a deployed bundle.
  adminPriceAdjustmentEnabled:
    process.env['REACT_APP_ADMIN_PRICE_ADJUSTMENT_ENABLED'] === 'true',
  // Feature flag: eager landing prefetch on the logo list. When "true", the
  // logo list fires its design-list warm-up (designs first page + styles +
  // manufacturers) at MOUNT — in parallel with the heavy GET /api/logos call —
  // instead of only after that call has landed, and additionally warms the
  // folder tree (Vendor/Admin only, same key/guards as useFolders) that
  // otherwise waits for the whole logos+options+organizations gate. Default
  // (unset/"false") = the exact current behavior (warm-up waits for logos).
  // CI builds don't read .env.dev — the flag must be set in the workflow yml
  // env block to reach a deployed bundle.
  eagerPrefetchEnabled:
    process.env['REACT_APP_EAGER_PREFETCH_ENABLED'] === 'true',
  // Feature flag: "Confirm order" design lift (Forslag B) — card layout for the
  // delivery form below the cart plus a sticky order summary side panel with the
  // action buttons. PURE redesign: every handler, validation rule, payload field
  // and gate is byte-identical to the classic layout. Default (unset/"false") =
  // the exact current UI (the classic code path is untouched and still renders).
  // CI builds don't read .env.dev — the flag must be set in the workflow yml env
  // block to reach a deployed bundle.
  orderConfirmDesignEnabled:
    process.env['REACT_APP_ORDER_CONFIRM_DESIGN_ENABLED'] === 'true',
  // Feature flag: "I'll pick up myself" on the confirm-order page. Only has an
  // effect together with orderConfirmDesignEnabled (the pickup cards render in
  // the new design only). When pickup is chosen the payload's shippingService
  // becomes "AFHENT" instead of GLSNORM/GLS12; nothing else in the payload
  // changes. DO NOT enable until NAV's handling of ShippingService="AFHENT" is
  // confirmed. Default (unset/"false") = no pickup UI, shippingService exactly
  // as today. CI builds don't read .env.dev — the flag must be set in the
  // workflow yml env block to reach a deployed bundle.
  pickupEnabled: process.env['REACT_APP_PICKUP_ENABLED'] === 'true',
  // Feature flag: "Ready for pickup" status text for PICKUP orders. A pickup
  // order whose internal status is "Order Shipped" (status job / feed /
  // storage / tracking all key on that value - it is NOT changed) is shown to
  // the customer as "Ready for pickup" in the order list and as "Ready for
  // pickup at Jet Sport" in the Delivery panel's green bar (pickup orders
  // never get track & trace). Pickup is detected via deriveDeliveryMode
  // (shippingService AFHENT/ALM/PICKUP - blank is NOT pickup). Display-only.
  // Default (unset/"false") = byte-identical to today (Nichlas 7/9-2026).
  // CI builds don't read .env.dev - set it in the workflow yml.
  pickupReadyStatusEnabled:
    process.env['REACT_APP_PICKUP_READY_STATUS_ENABLED'] === 'true',
  // Feature flag: admin act-as on DISABLED organizations. The ProfileContext
  // disabled-gate force-logs out whoever's EFFECTIVE profile org is disabled —
  // under impersonation that is the act-as target, so admins could never place
  // an order on a disabled customer's behalf. With this flag on, the forced
  // logout only fires for the user's OWN login org; an act-as target may be
  // disabled. The customer's own users stay locked out exactly as before.
  // Default (unset/"false") = current behavior. CI builds don't read .env.dev —
  // the flag must be set in the workflow yml env block to reach a deployed bundle.
  actAsDisabledOrderEnabled:
    process.env['REACT_APP_ACT_AS_DISABLED_ORDER_ENABLED'] === 'true',
  // Feature flag: design lift of the "Create new logo" dialog — webshop-style
  // modal chrome (border/shadow/rounding + proper close button + subtitle),
  // lifted option tiles (both copies: LocalButton in the legacy design editor
  // AND MenuButton in designer2, so they stay identical), step-2 fields and
  // drop-zone in the project palette, plus a new "Back to options" button from
  // step 2 to the tile picker. Default (unset/"false") = the exact current
  // look and behavior. CI builds don't read .env.dev — the flag must be set in
  // the workflow yml env block to reach a deployed bundle.
  logoDialogDesignEnabled:
    process.env['REACT_APP_LOGO_DIALOG_DESIGN_ENABLED'] === 'true',

  // Feature flag: "Ret levering" (edit delivery) on the order pages. When
  // "true": the order list renames "Est. Shipment Date" to "Expected
  // delivery" and adds a T&T column (tracking link), and the order details
  // screen gets a Delivery panel with an "Edit delivery" button + dialog that
  // calls the new shipping-address/shipping-agent endpoints (built in the API
  // in parallel). Default (unset/"false") = the pages are 100 % identical to
  // today. CI builds don't read .env.dev — the flag must be set in the
  // workflow yml env block to reach a deployed bundle.
  retLeveringEnabled: process.env['REACT_APP_RET_LEVERING_ENABLED'] === 'true',
  // Feature flag: "Note on design" — a customer-editable note shown as a
  // post-it (Nichlas' own drawing) pinned in the design details popup's
  // top-right corner; clicking it opens the note as a popover styled as the
  // same post-it. Shown even when prices are hidden. Paired with the API flag
  // DesignNoteEnabled (the PUT endpoint answers 404 while that is off; the
  // post-it then hides itself instead of erroring). Default (unset/"false") =
  // the popup is 100 % identical to today. CI builds don't read .env.dev — the
  // flag must be set in the workflow yml env block to reach a deployed bundle.
  designNoteEnabled: process.env['REACT_APP_DESIGN_NOTE_ENABLED'] === 'true',
  // Feature flag: order-view logo image self-heal. When "true", the order
  // line's logo preview (ProductPreviewLogo) fetches its detail logo with the
  // CORRECT org id (getLogoOrgId instead of the undefined-for-vendor/shared
  // customerId), runs the bounded high-res heal (5s/15s/45s, same
  // useHealPendingHighRes as the logo editor/details popup) while the sharp
  // render is still pending, and shows a small "Preparing image" pill on the
  // image until the genuine high-res lands. Default (unset/"false") = the
  // exact current behavior and DOM (raw img, no pill, no heal, old org-id
  // bug included). CI builds don't read .env.dev — the flag must be set in
  // the workflow yml env block to reach a deployed bundle.
  logoImageHealEnabled:
    process.env['REACT_APP_LOGO_IMAGE_HEAL_ENABLED'] === 'true',
  // Feature flag: multi-delete in the design list. When "true", the
  // multi-select dropdown (the "(N)" share button next to Download PDF /
  // Send via Email) gets a destructive "Delete selected (N)" item that runs a
  // DOUBLE confirmation (Nichlas' explicit requirement 19/8: it must ask
  // twice) and then deletes sequentially via the existing single-delete
  // endpoint (DELETE organizations/{orgId}/designs/{designId}) — exact same
  // rules as the row-level delete button: only rows that show a delete button
  // today are deletable, the rest are skipped and said out loud in dialog 1.
  // Default (unset/"false") = the menu is 100 % identical to today. CI builds
  // don't read .env.dev — the flag must be set in the workflow yml env block
  // to reach a deployed bundle.
  multiDeleteEnabled: process.env['REACT_APP_MULTI_DELETE_ENABLED'] === 'true',
  // Feature flag: multi-delete in the LOGO list - the logo-list twin of
  // multiDeleteEnabled above (same double-confirmation UX, same sequential
  // delete via the existing single-delete endpoint DELETE
  // organizations/{orgId}/logos/{logoId}). Separate flag so the new surface
  // can be rolled back without touching the proven design-list one. Default
  // (unset/"false") = the logo list is 100 % identical to today. CI builds
  // don't read .env.dev - set it in the workflow yml env block.
  multiDeleteLogosEnabled:
    process.env['REACT_APP_MULTI_DELETE_LOGOS_ENABLED'] === 'true',
  // Feature flag: admin-only "Keep open in KitMaker" override on the
  // organization edit screen (Status section) — a checkbox that exempts the
  // organization from the eSeller/NAV closure sync (the org stays open in
  // KitMaker even when the customer is disabled in eSeller). Paired with the
  // API flag OrgKeepEnabledOverrideEnabled (with that off the field is
  // silently ignored server-side) — only enable here where the API flag is
  // ON. Default (unset/"false") = checkbox invisible AND the field omitted
  // from the save payload (wire-identical to before, so an old bundle can
  // never clear an admin-set override). CI builds don't read .env.dev — the
  // flag must be set in the workflow yml env block to reach a deployed bundle.
  keepEnabledOverrideEnabled:
    process.env['REACT_APP_KEEP_ENABLED_OVERRIDE_ENABLED'] === 'true',
  // Feature flag: admin settings-only save on the org edit screen (both the
  // customer form and the vendor form /vendor/:id/edit). With "true": (1) an
  // ADMIN who only changed admin settings (keep open in KitMaker, hide
  // prices, deactivate) can save even when the org's company/contact fields
  // are incomplete (NAV-synced orgs often have no primary contact, and the
  // vendor orgs closed by the eSeller/NAV sync are the keep-open override's
  // target) — the unchanged fields are sent back exactly as loaded; (2) when
  // the validation still blocks, the toast lists WHICH fields are missing
  // instead of the generic text. Non-admins and any save that touches other
  // fields keep the full validation. The API accepts the payload (only the
  // name is required server-side); pair with the API flag
  // OrgBlankContactGuardEnabled so a save without contact details does not
  // create a blank primary-contact row (the e-mail recipient fallback would
  // otherwise treat "" as an address). Default (unset/"false") = the exact
  // current behavior and toast text. CI builds don't read .env.dev — set in
  // the workflow yml env block.
  customerUpdatePartialSaveEnabled:
    process.env['REACT_APP_CUSTOMER_UPDATE_PARTIAL_SAVE_ENABLED'] === 'true',
  // Feature flag: customer-owned style uploads. When "true": (1) the
  // create-design "Upload your own images" POST goes to the org the design is
  // created FOR (Vendor / admin act-as: the selected customer; Admin: the
  // picked vendor; EndUser: ALWAYS its own org) with fallback to the logged-in
  // org when nothing is selected yet; (2) an owned upload that was selected
  // for a different customer is deselected (info toast) when the target
  // customer/vendor changes; (3) the Change Style screen gets the same upload
  // dialog (owner = design.customerId). Paired with the API app setting
  // VendorSeesCustomerUploadsEnabled (vendor sees its customers' uploads in
  // the chooser + upload-target guard) — only enable here where that is ON.
  // Valid combinations: both off = wire- and SQL-identical to today; API on /
  // FE off = harmless (uploads still land on the logged-in org); FE on / API
  // off = accepted degraded state (customer-owned uploads vanish from the
  // vendor's chooser after refetch) — hence API first. Default
  // (unset/"false") = the exact current behavior incl. NO GET
  // /styles/categories request on Change Style (the dialog is not mounted).
  // CI builds don't read .env.dev — the flag must be set in the workflow yml
  // env block to reach a deployed bundle.
  customerOwnedUploadsEnabled:
    process.env['REACT_APP_CUSTOMER_OWNED_UPLOADS_ENABLED'] === 'true',
  // Feature flag: storage-balance line on the extra-logo (partly heating)
  // cards - "In storage / used by this order / after this order" (case
  // 20006106: the step showed NOTHING about storage, so a customer with 107
  // in stock read it as "no stock"). Display-only arithmetic - nothing is
  // persisted. Default (unset/"false") = the card is 100 % identical to
  // today. CI builds don't read .env.dev - set it in the workflow yml.
  storageBalanceEnabled:
    process.env['REACT_APP_STORAGE_BALANCE_ENABLED'] === 'true',
  // Feature flag: "Requested delivery" on the order pages. The API has always
  // overwritten Order.LatestDelivery with NAV's shipment date (at placement
  // and again from the status jobs), so the date the customer picked at
  // checkout was lost from every screen. Paired with the API PR that
  // persists that pick and exposes it as OrderDto.requestedDelivery. When
  // "true": the order list's delivery-date cell and the order details'
  // Delivery panel add "Requested {date}" - ONLY when it differs from the
  // expected date - and the read-only order view (the receipt) shows the
  // customer's own pick with a small "Expected {date}" note when they differ.
  // Display-only; nothing is sent. Default (unset/"false") = byte-identical
  // to today. CI builds don't read .env.dev - set it in the workflow yml.
  requestedDeliveryEnabled:
    process.env['REACT_APP_REQUESTED_DELIVERY_ENABLED'] === 'true',
  // Feature flag: checkout storage verify (order 20007070: the cart showed a
  // STALE storage count - 194 cached, 144 in NAV - so the customer saw "all
  // logos in storage" and a price without start-up fees; placement itself
  // was correct). When "true" the confirmation step calls
  // POST organizations/{orgId}/orders/verify-storage ONCE per entry (local
  // draft only, 8 s timeout, failures are silent), patches the fresh counts
  // into the checkout items + cart, rebuilds the local draft without any
  // further API call and shows a non-blocking yellow notice above the place
  // button when a logo draws less from storage than the cart showed. Paired with
  // the API flag CheckoutStorageVerifyEnabled - API first (with it off the
  // API answers "not verified" and nothing changes). Default (unset/"false")
  // = byte-identical to today. CI builds don't read .env.dev - set it in the
  // workflow yml.
  checkoutStorageVerifyEnabled:
    process.env['REACT_APP_CHECKOUT_STORAGE_VERIFY_ENABLED'] === 'true',
  // Feature flag: stock freshness on view (Nichlas 7/9: "the count on the
  // front page must be correct"). A storage count is never shown from a row
  // that is missing, never stamped or older than 24 h without being refreshed
  // in the same moment: the logo list and the logo detail make ONE
  // POST organizations/{orgId}/logos/storage-freshness call for exactly the
  // stale logos on screen (debounced 1 s, at most once per logo per 5 min,
  // 8 s timeout, failures are silent) and patch the fresh counts + stamps
  // into the react-query cache, so the rows read "Stock updated just now".
  // The page never waits. Also shows the "Stock updated ..." line under the
  // list's storage count. Paired with the API flag
  // StorageFreshnessOnViewEnabled - API first (with it off the API answers
  // cached figures, nothing changes). Default (unset/"false") =
  // byte-identical to today. CI builds don't read .env.dev - set it in the
  // workflow yml.
  storageFreshnessEnabled:
    process.env['REACT_APP_STORAGE_FRESHNESS_ENABLED'] === 'true',
  // Feature flag: manual "refresh stock now" button next to a storage count
  // (Nichlas 8/9: the customer must be able to refresh ONE item from NAV
  // right away, whatever the stamp's age). One click = ONE
  // POST organizations/{orgId}/logos/storage-freshness call with
  // {logoIds:[id], force:true}; the button is then locked for 5 minutes per
  // (org, logo) - the API's dedupe window - and shares the org budget of the
  // freshness-on-view call. Requires storageFreshnessEnabled (same cache
  // patch, same guard) and is paired with the API flag
  // StorageFreshnessManualEnabled - API first (with it off, force is treated
  // as false and the button locks itself). Default (unset/"false") = no
  // button = byte-identical to today. CI builds don't read .env.dev - set it
  // in the workflow yml.
  storageManualRefreshEnabled:
    process.env['REACT_APP_STORAGE_MANUAL_REFRESH_ENABLED'] === 'true',
  // Feature flag: stock look-up on add-to-cart (Nichlas 8/9: "when something
  // is put in the cart, can it do a quick look-up without hurting
  // performance or the user?"). The line is added AT ONCE with the count the
  // page already knows - nothing waits, nothing blocks - and afterwards ONE
  // non-blocking POST organizations/{orgId}/logos/storage-freshness with
  // {logoIds, force:true} leaves for exactly the logos just added (a design
  // = its logos, one call). The answer is patched into the cart's own copy
  // of the logo and the react-query cache, so the cart line's count and
  // "Stock updated just now" follow a moment later. Silent on every
  // outcome: no toast, 8 s timeout, "not verified" left alone. Shares the
  // 5-minute per-(org, logo) guard with the on-view call and the manual
  // button (a second add of the same logo costs nothing), and their org
  // budget. No call on quantity changes, removals or re-renders. Paired with
  // the API flag StorageFreshnessManualEnabled (force) - API first. Default
  // (unset/"false") = byte-identical to today. CI builds don't read .env.dev
  // - set it in the workflow yml.
  storageRefreshOnAddToCartEnabled:
    process.env['REACT_APP_STORAGE_REFRESH_ON_ADD_TO_CART_ENABLED'] === 'true',
  // Feature flag: Stripe payouts (Nichlas 8/9: Stripe's payout mail "4,291.15kr
  // is on the way … po_…" cannot be tied to KitMaker order numbers). Adds the
  // admin page "Stripe payouts" (list with date range + search, detail with
  // the order table + CSV download, lookup of po_/pi_/ch_/cs_/order number)
  // and a small "Stripe payment pi_… · payout po_… (arrived …)" line on the
  // order details for admins/vendors. Paired with the API flag
  // StripePayoutReconciliationEnabled - API first (with it off the stripe/*
  // routes answer 404 and the page shows "not active"). Default
  // (unset/"false") = byte-identical to today: no menu item, no routes, no
  // line. CI builds don't read .env.dev - set it in the workflow yml.
  stripePayoutsEnabled:
    process.env['REACT_APP_STRIPE_PAYOUTS_ENABLED'] === 'true',
  // Feature flag: approval response-patch + button lock (analysis
  // ANALYSE-GODKENDELSE-HASTIGHED-2026-09-09 R3 del A). Every design verdict
  // used to invalidate the whole ["designs"] cache: GetDesigns (prod p50
  // 4.6 s, p95 19 s) + GetDesign + one GetDesign per design in the cart, and
  // reject+delete did it twice. When "true": DesignRepository.placeVerdict /
  // initiateApproval / skipApproval patch the server's DesignDto into the
  // detail entry, every list page and the cart's entries in place. Prices:
  // a container in the response that holds an ADJUSTED price (adjustedPrice
  // > 0 - only AdjustPrice sets it, on the MUL-creating commit path:
  // approval-start, skip, the vendor approval that creates the MUL) wins;
  // otherwise the cached prices stay (a reject, and any approval of a design
  // that already has its MUL, answer with persisted-only rows: adjustedPrice
  // 0, empty totalPrices - no information). Storage counts always
  // stay from the cache. A patch never extends freshness, a status-filtered
  // page drops a row that no longer matches, and a query that is fetching
  // while the patch lands gets the patch re-applied when it lands.
  // deleteDesign removes the row in place. A failure inside the patch falls
  // back to today's invalidate (the PUT/DELETE has already succeeded). Same
  // flag: Approve/Reject are disabled while the PUT is in flight (logo
  // popup: isLoading reaches ApprovalVoting + the modal overlay; design
  // popup: the verdict is awaited with the overlay on and the popup closes
  // on success). No supplier call is touched. Default (unset/"false") =
  // byte-identical to today: invalidate + refetch, unlocked buttons, no
  // disabled attribute on the popup's buttons. CI builds don't read
  // .env.dev - set it in the workflow yml.
  approvalPatchEnabled:
    process.env['REACT_APP_APPROVAL_PATCH_ENABLED'] === 'true',
  // Feature flag: design SAVE response-patch (I5b, analysis 10/9). The
  // editor's Save and Approve buttons both PUT .../designs/{id}, and
  // DesignRepository.updateDesign then invalidates the whole ["designs"]
  // cache: GetDesigns (prod p50 4.6 s) + GetDesign + the cart's entries are
  // refetched before the approval dialog even opens. When "true" and the
  // response's placed-logo set is the one the detail cache already holds
  // (a move/rotate/rename/measurement-line/title save, or the no-change save
  // Approve fires), the server's DesignDto is patched into the detail entry,
  // every list page and the cart's entries in place (same helpers and price
  // rule as approvalPatchEnabled: the save never runs AdjustPrice, so the
  // cached prices stay; storage stays; positions, lines, title, thumbnail
  // and updateAtUtc come from the response). A save that ADDS or REMOVES a
  // logo, or a save without a cached detail entry (list-side saves), keeps
  // today's invalidate - its prices need the server's AdjustPrice. A failure
  // inside the patch falls back to today's invalidate (the PUT has already
  // succeeded). No supplier call is touched. Default (unset/"false") =
  // byte-identical to today: invalidate + refetch. CI builds don't read
  // .env.dev - set it in the workflow yml.
  designSavePatchEnabled:
    process.env['REACT_APP_DESIGN_SAVE_PATCH_ENABLED'] === 'true',
  // Feature flag: client correlation headers. When "true" authFetch adds
  // x-km-session (one random id per tab, kept in sessionStorage) and
  // x-km-click (the id of the last user mutation, attached until the
  // mutation settles + 2 s, at most 10 s after markClick) to every API
  // request, so the refetch cascade per click can be proven in App Insights
  // (ClientIP is shared and SessionId empty there).
  // Paired with the API flag ClientCorrelationTelemetryEnabled (with it off
  // the headers are simply ignored). Custom headers ride on the CORS
  // preflight - verify on dev before prod. Default (unset/"false") = no
  // extra headers = byte-identical requests. CI builds don't read .env.dev -
  // set it in the workflow yml.
  clientCorrelationEnabled:
    process.env['REACT_APP_CLIENT_CORRELATION_ENABLED'] === 'true',
  // Feature flag: logo-list "send for approval" fix. The list flow called
  // PUT organizations/{orgId}/logos/{id}/commit BEFORE approval-start - a
  // route that does not exist in the API (only orders/{id}/commit does), so
  // authFetch threw on the 404 and approval-start was never reached. When
  // "true" the commit call is skipped and approval-start is called directly
  // (the API commits itself in PlaceVerdictAsync when the serial is
  // missing). Default (unset/"false") = today's two calls (commit first).
  // CI builds don't read .env.dev - set it in the workflow yml.
  approvalListFlowFixEnabled:
    process.env['REACT_APP_APPROVAL_LIST_FLOW_FIX_ENABLED'] === 'true',
  // Old-webshop migration landing: comma-separated list of hostnames (e.g.
  // "webshop2.jetsport.dk") that serve the migration lookalike page as their
  // FRONT PAGE (root route) instead of the normal login-gated app. Empty or
  // unset = normal routing on every host — the /public/migration route exists
  // regardless, so dev tests the page there. CI builds don't read .env.dev —
  // the value must be set in the workflow yml env block to reach a deployed
  // bundle; prod gets "webshop2.jetsport.dk" with the prod release (the prod
  // workflow is deliberately untouched until then).
  migrationHosts: (process.env['REACT_APP_MIGRATION_HOSTS'] ?? '')
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean),
  // Feature flag: optimistic logo editor (Nichlas 9/9: upload + colour/size
  // changes feel too slow). Upload shows the LOCAL file in the canvas at
  // once (object URL for images, a file card for pdf/ai/eps) with a thin
  // progress line instead of the full-screen spinner; colour change keeps
  // the current image visible with from->to chips + a progress line instead
  // of the modal overlay (buttons stay disabled); the mm ruler and the
  // canvas scale follow the size fields while typing. Same LogoEditorTool in
  // the design editor. 0 new API calls. Default (unset/"false") = the exact
  // current overlays and DOM. CI builds don't read .env.dev - set it in the
  // workflow yml.
  logoEditorOptimisticPreviewEnabled:
    process.env['REACT_APP_LOGO_EDITOR_OPTIMISTIC_PREVIEW'] === 'true',
  // Feature flag: high-res heal PER MUTATION in the logo editor. Today the
  // bounded heal (useHealPendingHighRes) only resets its 3-poll budget when
  // the logo id changes, and the standalone upload path has no heal at all,
  // so a colour/size change leaves the 180px mutation image on the canvas
  // until F5. With the flag the editor view-models seed the detail cache
  // with each mutation response, subscribe to it (no extra GetLogo: seeded
  // entry + staleTime Infinity), restart the poll budget after every
  // mutation (last mutation wins) and patch ONLY the image/size fields of the
  // open logo when a sharper render lands. The screen-level heal is handed
  // over to the view-model so there is never a double budget. Budget: max 3
  // GetLogo per mutation. Default (unset/"false") = byte-identical.
  logoHealPerMutationEnabled:
    process.env['REACT_APP_LOGO_HEAL_PER_MUTATION'] === 'true',
  // Optional poll schedule for the heal above (only read when
  // logoHealPerMutationEnabled is on): "3000,8000,15000" in ms. Invalid or
  // unset = 5000,15000,45000; capped to 3 entries (owner budget).
  healDelaysMs: parseHealDelaysMs(process.env['REACT_APP_HEAL_DELAYS_MS']),
  // Feature flag: clone/upload size fix in the editor canvas (FE cause found
  // 9/9): mutation responses carry a 180px bitmap that object-scale-down
  // paints 180px wide inside the >=450px canvas box, while LogoRuler measured
  // the BOX - the logo looked ~40 % too small against the mm ruler until the
  // 1000px render arrived. With the flag the ruler measures the PAINTED
  // rectangle (contain-fit of naturalWidth/naturalHeight) and the canvas uses
  // object-contain so the logo keeps one size before and after the heal.
  // Pair with logoHealPerMutationEnabled (the upscaled 180px is soft until
  // the heal lands). Default (unset/"false") = byte-identical.
  logoCloneSizeFixEnabled:
    process.env['REACT_APP_LOGO_CLONE_SIZE_FIX'] === 'true',
  // Feature flag: "Prices on their way" (R5, ANALYSE-UPLOAD-SKARPHED
  // 10/9 trin 4). The API moves the supplier product/price look-up out of
  // the upload/resize/colour/clone request into a queue worker and marks
  // the logo productPending (design: pricePending) until the worker has
  // written the fresh product. When "true": the logo popup/list, the design
  // popup/list, the cart and the confirmation page show an amber "Prices on
  // their way" badge instead of the (old or missing) prices, Approve /
  // Send for approval / Skip approval / Add to order / Continue / Place
  // order are disabled with a tooltip while pending, the open popup / cart
  // re-reads ONLY its own price endpoint (GetLogo / GetDesign) on a bounded
  // 3/8/15 s schedule (max 3 calls per pending view, never GetAllLogos),
  // and a 409 productPending from commit/approval/order is answered with an
  // info toast and ONE automatic retry after 3 s. Paired with the API flags
  // LogoProductRefreshEnabled + MutationProductDeferredEnabled - API first
  // (with them off the fields are simply absent = nothing is pending).
  // 11/9 (list refresh): the badge is a 10 px status line in the pending
  // tone with a pulsing dot (no pill), a landed GetLogo (popup poll, editor
  // heal, cart sync) is copied into the cached LIST rows of the same logo
  // (patchLogoRowsFromDetail, 0 calls) and the logo list re-reads its own
  // pending rows on the same 3/8/15 s plan (useLogoListPendingPoll: <= 3
  // GetLogo per pending row younger than 10 min, or ONE list refetch per
  // tick above 3 pending rows) - the return-to-list refetch races the
  // worker and the badge otherwise stayed until F5 (prod 675889).
  // Default (unset/"false") = byte-identical: no badge element, no
  // disabled attribute, no extra call, no retry, no cache write. CI builds
  // don't read .env.dev - set it in the workflow yml.
  productPendingEnabled:
    process.env['REACT_APP_PRODUCT_PENDING_ENABLED'] === 'true',
  // Optional poll schedule for the pending re-read above (only read when
  // productPendingEnabled is on): "3000,8000,15000" in ms. Invalid or unset
  // = 3000,8000,15000; capped to 3 entries (owner budget, same parser and
  // cap as REACT_APP_HEAL_DELAYS_MS).
  productPendingPollMs: parseHealDelaysMs(
    process.env['REACT_APP_PRODUCT_PENDING_POLL_MS'],
    DEFAULT_PRODUCT_PENDING_POLL_MS
  ),
  // Feature flag: "instant clone" (K3, design 11/9). The API answers the
  // copy call from the source alone (0 supplier calls) and marks the clone
  // uploadPending until its queue worker has fetched the clone's own
  // supplier identity; every action that needs that identity (approve /
  // send / skip / add to order / continue / place order, colour, resize,
  // method change) is locked or answered 409 productPending with reason
  // "cloneUpload" meanwhile. When "true": the clone flow seeds the editor
  // straight from the copy response (source size + inline high-res, no
  // size-restore resize, no heal polls), the popup / list / editor show a
  // "Preparing logo…" badge (after 10 minutes "Logo preparation delayed"),
  // the locks above apply, colour / resize / method change answer a 409
  // cloneUpload with an info toast + ONE retry, and the open popup / editor
  // re-read ONLY their own GetLogo on the bounded 3/8/15 s schedule until
  // the badge clears. Prices are never hidden (the clone carries its
  // source's prices). Paired with the API flag CloneInstantEnabled - API
  // first (with it off the fields are simply absent = nothing is pending).
  // Works with or without REACT_APP_PRODUCT_PENDING_ENABLED. 11/9 (list
  // refresh): the "Preparing logo…" badge shares the restyled status line,
  // the landed clone identity is copied into the cached list rows
  // (patchLogoRowsFromDetail, 0 calls) and the logo list re-reads its own
  // upload-pending rows on the bounded plan (useLogoListPendingPoll, <= 3
  // GetLogo per pending row younger than 10 min). Default (unset/"false")
  // = byte-identical: no badge element, no disabled attribute, no extra
  // call, no retry, no cache write. CI builds don't read .env.dev - set it
  // in the workflow yml.
  cloneInstantEnabled:
    process.env['REACT_APP_CLONE_INSTANT_ENABLED'] === 'true',
  // Feature flag: "upload stands alone" (design 14/9, API flag
  // UploadPendingEnabled). When app117 is down or slow the API stores the
  // original + the row FIRST and answers the upload at once (<= 0.7 s) with
  // uploadPending=true + uploadPendingKind "upload" instead of hanging 300 s
  // and failing; its worker fetches serial / colours / method / high-res
  // when the supplier answers. When "true": the New logo dialog paints the
  // user's own file (object URL for raster, a file card for pdf/ai/eps)
  // instead of the spinner, such a pending answer opens the editor on that
  // file (never replaced by anything less sharp - keepSharp) or on a file
  // card, the toolbar shows "Preparing logo…", colour / method / size /
  // Save & approve are locked with a tooltip (title, customer, Save draft,
  // Cancel, Delete stay open), the editor re-reads ONLY its own GetLogo on
  // 2/2/3/3 s (max 4) plus at most 2 on-demand re-reads when a locked
  // control is touched (1 per 10 s), and the worker's landing is patched in
  // (serial, mm, colours, the worker's method choice, the high-res) so the
  // locks open without F5. A deterministic rejection (uploadFailedReason:
  // "Logo size is too big", "Too many artboards", no serial) shows a modal
  // with "Upload another file" / "Back to logos" (ONE DELETE of the dead
  // row) and a red "Upload failed" line in the list / popup. Needs
  // REACT_APP_CLONE_INSTANT_ENABLED (the pending state) and
  // REACT_APP_LOGO_HEAL_PER_MUTATION (the subscription that delivers the
  // re-reads to the editor); REACT_APP_LOGO_KEEP_SHARP is recommended ON
  // as on main's prod yml - the user's own registered file is kept on the
  // canvas until a genuine high-res lands whatever that flag says, but the
  // ordinary (non-pending) upload's F2 hold still depends on it. Paired
  // with the API flag UploadPendingEnabled -
  // API first (with it off the fields are simply absent = nothing is
  // pending, today's upload). Default (unset/"false") = byte-identical DOM
  // and wire. CI builds don't read .env.dev - set it in the workflow yml.
  uploadPendingEnabled:
    process.env['REACT_APP_UPLOAD_PENDING_ENABLED'] === 'true',
  // Feature flag: "keep sharp" in the logo editor (step 1 of the upload
  // sharpness plan, 10/9). A mutation response never carries the 1000px
  // render (its key changes with every mutation and the worker only starts
  // at the response), so today every resize/title/upload/clone response
  // swaps the sharp canvas bitmap for an upscaled 180px thumbnail until the
  // heal lands. With the flag: (F1) a response or heal poll without a
  // genuine high-res never replaces a sharp bitmap already shown for the
  // same logo - only mm/product/colours are patched (colour and method
  // changes alter the artwork and behave exactly as today); (F2) an upload
  // keeps the user's own file as the canvas bitmap until the real high-res
  // lands or the heal budget is spent (pdf/ai/eps as today); (F3) a clone
  // opens with the SOURCE's high-res (already cached from the popup) until
  // its own lands, and the extra GetLogo after the size restore is only
  // fired when the restore left the entry pending. 0 new API calls, -1
  // GetLogo per clone. Pair with logoHealPerMutationEnabled (the heal is
  // what delivers the sharp render). Default (unset/"false") =
  // byte-identical. CI builds don't read .env.dev - set it in the workflow yml.
  logoKeepSharpEnabled: process.env['REACT_APP_LOGO_KEEP_SHARP'] === 'true',
  // Feature flag: drag the corner of the logo to change its size in the
  // logo editor. A handle sits on the bottom-right corner of the painted
  // bitmap; dragging previews the size into the SAME size fields the
  // "Change size" button reads (fields, mm ruler and canvas scale follow),
  // releasing makes ONE resize call - none when the size is unchanged or
  // the drag is cancelled (Esc). Arrow keys nudge 1 mm (Shift 10), Enter
  // applies. 0 new endpoints. Needs logoEditorOptimisticPreviewEnabled (the
  // live canvas scale) - without it the handle is not rendered. Default
  // (unset/"false") = byte-identical DOM. CI builds don't read .env.dev -
  // set it in the workflow yml.
  logoDragResizeEnabled: process.env['REACT_APP_LOGO_DRAG_RESIZE'] === 'true',
  // Feature flag: keep the dragged scale after a corner drag in the logo
  // editor. Without it the canvas paints the logo in its fit box for the
  // SAVED size, so a drag that makes the logo smaller shrinks it on screen
  // and it jumps back to the fit size the moment the resize response lands.
  // When "true" the canvas keeps the px/mm the drag ran at (anchored at the
  // saved size when the drag started; never larger than the fit box) and a
  // "Fit to view" button in the canvas corner toggles between that true
  // scale and the fit view. Opening the editor is the fit view as today;
  // size fields, badge and ruler follow the mm as before. Pure view state:
  // 0 new endpoints, no extra resize call when the view is toggled. Needs
  // logoDragResizeEnabled (and through it the optimistic preview) - without
  // them nothing changes. Default (unset/"false") = byte-identical DOM. CI
  // builds don't read .env.dev - set it in the workflow yml.
  logoDragResizeKeepScaleEnabled:
    process.env['REACT_APP_LOGO_DRAG_RESIZE_KEEP_SCALE'] === 'true',
  // Feature flag: "In storage" line per logo in the design detail popup's
  // "Logos in Design" cards (and the same card in the design editor's logo
  // panel). Customer ask 11/9: "does the print exist in stock?" without
  // opening every logo. The count is the inStorage the design's logo already
  // carries (GetDesign maps it from the org-scoped StorageCounts row, same
  // figure as the logo list/detail) and the "x min ago" caption is the same
  // StorageUpdatedAt as the logo detail - 0 new calls. A logo without a
  // known count shows no line. Default (unset/"false") = byte-identical DOM.
  // CI builds don't read .env.dev - set it in the workflow yml.
  designLogoStorageLineEnabled:
    process.env['REACT_APP_DESIGN_LOGO_STORAGE_LINE'] === 'true',
  // Flag REACT_APP_LOGO_POPUP_FAST: the logo popup (detail dialog opened
  // from the logo list) opens on the list row's data and is patched in place
  // when the background GetLogo lands, instead of today's blur placeholder +
  // full remount. Measured prod 7 d (2026-09-04..11): GetLogo p50 0.57 s,
  // p95 2.7 s, p99 7.2 s; 9 % of the calls carry a synchronous supplier
  // render/price lookup (p50 2.2 s, p95 11 s) - the popup showed a BLURRED
  // thumbnail (ImageLogo) or the 180px stub stretched (name/number logos) for
  // that whole time and then repainted from scratch. With the flag: (1) the
  // detail cache entry (["logos", actAs, id]) is seeded from the list row
  // when it is empty, marked as "from the list" (detailFromList) so the
  // heal/pending polls and designCount never judge the stub, and ONE
  // background GetLogo refreshes it (same call as today, never more);
  // (2) the thumbnail paints crisp at once and the high-res crossfades in on
  // arrival - no blur, no remount, no state reset; (3) buttons that need
  // the detail (Download PDF = high-res, View designs = designCount) still
  // wait for it, everything else (Approve, Add to order, Send, Skip) is live
  // from the first frame as before. 0 new API calls. Default
  // (unset/"false") = byte-identical. CI builds don't read .env.dev - set it
  // in the workflow yml.
  logoPopupFastEnabled: process.env['REACT_APP_LOGO_POPUP_FAST'] === 'true',
  // Flag REACT_APP_LOGO_PRICE_REFRESH_NOW_ENABLED: admin "Refresh prices
  // now" icon button in the logo popup's price column (image logos only;
  // design 12/9-2026). One click = ONE POST
  // organizations/{orgId}/logos/{logoId}/product-refresh: the API evicts
  // the NAV FindTemplate2 + JetSport GetTemplate caches for the logo, stamps
  // it product-pending and re-queues the existing price worker (+ the
  // serial's price batch). The detail and list cache entries are patched to
  // "Prices on their way" from the answer (0 calls) and the existing pending
  // poll (REACT_APP_PRODUCT_PENDING_ENABLED, <= 3 GetLogo) clears the badge
  // when the worker has landed - exactly as after a resize. 60 s cooldown
  // per logo (the API's 429 carries the exact seconds), 0 new GET calls,
  // nothing on render or on an effect. Paired with the API flag
  // LogoPriceRefreshNowEnabled - API first (with it off the route answers
  // 404 and a click shows the "could not be refreshed" toast). Default
  // (unset/"false") = the button does not exist = byte-identical DOM and
  // wire. CI builds don't read .env.dev - set it in the workflow yml.
  logoPriceRefreshNowEnabled:
    process.env['REACT_APP_LOGO_PRICE_REFRESH_NOW_ENABLED'] === 'true',
  // Flag REACT_APP_LOGO_PRICE_REFRESH_NOW_EDITOR_ENABLED: the SAME admin
  // "Refresh prices now" button in the logo editor's toolbar (next to the
  // "Editing logo" heading; the standalone editor and the design editor's
  // logo tool share that toolbar). Sub-flag of
  // REACT_APP_LOGO_PRICE_REFRESH_NOW_ENABLED - BOTH must be "true" for the
  // editor button to exist; the popup button never reads this one. Same
  // POST, same cooldown, 0 calls on render. In the editor a click writes
  // ONLY the list rows (the "Prices on their way" mark for the list the
  // admin goes back to; its own poll clears it) - never the detail entry:
  // the editor's heal subscription hands every change of that entry to the
  // canvas sync (keepSharp, the held upload's poll budget, the hi-res heal
  // timer), which a pending mark must not touch. The editor's lock is
  // judged on the freshest cached detail (its own polls land there), read
  // through a subscription - never a fetch. Default (unset/"false") = no
  // editor button = byte-identical DOM and wire. CI builds don't read
  // .env.dev - set it in the workflow yml.
  logoPriceRefreshNowEditorEnabled:
    process.env['REACT_APP_LOGO_PRICE_REFRESH_NOW_EDITOR_ENABLED'] === 'true',
  // Feature flag: "sharp wait" for the instant clone (11/9). A clone made
  // from a NAV-imported source carries only the source's small processed
  // thumbnail (e.g. 64x26 px) and no stored high-res, so the editor opened
  // on it with that thumbnail stretched over the whole canvas until the
  // clone worker's render landed. When "true": while the open logo is
  // upload-pending and its image is not a genuine high-res, the canvas
  // paints the bitmap at its natural pixel size (max 1x, centred, never
  // upscaled) inside a light skeleton frame with "Preparing sharp
  // preview…", the clone's own GetLogo entry is re-read on a faster
  // 2/2/3/3/5/5/5/5 s schedule (max 8 calls, 30 s) instead of 3/8/15 s and
  // the per-mutation heal stands down meanwhile, the re-read stops the
  // moment a genuine high-res arrives, and ONE extra GetLogo follows 1 s
  // after uploadPending clears without a high-res (the API stores the
  // high-res right before it clears the stamp). When the high-res lands
  // the canvas switches to the full view on the same <img> (no flicker)
  // and the text goes. Same behaviour in the design editor's logo tool.
  // Rulers and size fields are untouched. Needs REACT_APP_CLONE_INSTANT_ENABLED
  // (the pending state) and REACT_APP_LOGO_HEAL_PER_MUTATION (the pending
  // mark on the seed + the subscription that delivers the polls). Default
  // (unset/"false") = byte-identical DOM and wire. CI builds don't read
  // .env.dev - set it in the workflow yml.
  cloneInstantSharpWaitEnabled:
    process.env['REACT_APP_CLONE_INSTANT_SHARP_WAIT'] === 'true',
  // Flag REACT_APP_CUSTOMER_ONBOARDING_ENABLED: customer onboarding on the
  // org edit screen's "Users & contact" section (customer AND vendor org).
  // When "true": an "Onboarding" column per user (last login / welcome mail
  // sent / no login yet, from the fields GET users?orgId already carries -
  // 0 new calls per view), a "Resend welcome mail" action (POST
  // organizations/{orgId}/users/{userId}/welcome-mail = 1 call per click,
  // only offered while the API says the user never logged in) and a "copy
  // to the primary contact" checkbox in the resend and Add user dialogs
  // (CreateUserDto.ccPrimaryContact, sent only with the flag on). Paired
  // with the API flags UserOnboardingColumnsEnabled / WelcomeMailResendEnabled
  // / WelcomeMailCcEnabled: with those off the fields come back null and the
  // column shows a neutral dash, no button. Default (unset/"false") =
  // byte-identical DOM and wire. CI builds don't read .env.dev - set it in
  // the workflow yml.
  customerOnboardingEnabled:
    process.env['REACT_APP_CUSTOMER_ONBOARDING_ENABLED'] === 'true',
  // Flag REACT_APP_USER_DEACTIVATE_ENABLED (design «kontakter» 23/10-2026):
  // on the "Users & contact" section (the customer edit screen and the
  // dealer's own profile) every user row gets a "Deactivate user" /
  // "Reactivate user" action (ONE PATCH organizations/{orgId}/users/{userId}/
  // status per click, then the same list refetch as hide-prices) and a
  // "Deactivated" chip read off the deactivatedUtc field GET users?orgId
  // already carries (0 new calls per view). A deactivated user is left out
  // of the admin act-as user list, and a caller the API refuses with 403
  // userDeactivated is logged out like a disabled organisation. PAIRED with
  // the API flags UserDeactivateColumnsEnabled / UserDeactivateEnabled (the
  // route answers 404 while those are off - said as a failed toast, never a
  // crash; the chip stays off because the field comes back null). Default
  // (unset/"false") = byte-identical DOM and wire. CI builds don't read
  // .env.dev - set it in the workflow yml.
  userDeactivateEnabled:
    process.env['REACT_APP_USER_DEACTIVATE_ENABLED'] === 'true',
  // Flag REACT_APP_NAV_CONTACT_SYNC_ENABLED (NAV contact mirror, Peters
  // contactApi 23/10-2026): a user row whose navContactSync field (GET
  // users?orgId; present only under the API flags NavContactSyncColumnsEnabled
  // + NavContactSyncEnabled + NavContactSyncMode=on) says not-mirrored or
  // failed carries a chip "Not mirrored in NAV: <code>" / "NAV mirror failed"
  // next to the name (tooltip: state + reason), pending a gray "NAV mirror
  // pending" chip; synced / deactivated show nothing. 0 new calls per view
  // (the field rides on the row). Retry is an admin ops route (POST
  // ops/nav-contacts/users/{userId}/retry), not a button here. Default
  // (unset/"false") = byte-identical DOM and wire. CI builds don't read
  // .env.dev - set it in the workflow yml.
  navContactSyncEnabled:
    process.env['REACT_APP_NAV_CONTACT_SYNC_ENABLED'] === 'true',
  // Flag REACT_APP_USER_ACTION_TOOLTIPS_ENABLED (Nichlas 23/10-2026: "museover
  // med alle ... på alle sprog"): the per-row action icons of the users list
  // ("Users & contact" on the customer edit screen and the dealer's own
  // profile: edit / delete / deactivate-reactivate / resend welcome mail /
  // password link) show their label as a visible MUI Tooltip on mouse hover
  // AND keyboard focus, in the viewer's language - the same i18n texts the
  // native title attribute carries today, no new keys. While the tooltip is
  // on, the native title is left off (one text, not two: the button keeps
  // its aria-label as the accessible name). 0 calls. Default (unset/"false")
  // = byte-identical DOM: the title attributes as today. CI builds don't
  // read .env.dev - set it in the workflow yml.
  userActionTooltipsEnabled:
    process.env['REACT_APP_USER_ACTION_TOOLTIPS_ENABLED'] === 'true',
  // Flag REACT_APP_USER_CREATE_MAIL_STATUS_ENABLED: "Add user" understands
  // the API's honest 502 welcomeMailNotSent (API flag
  // UserCreateHardeningEnabled: the user WAS created, the welcome mail was
  // not) - the list is refetched so the new row shows, the dialog closes
  // (the create promise RESOLVES) and a warning toast says to use "Resend
  // welcome mail". That button only exists with customerOnboardingEnabled
  // (the API flags UserOnboardingColumnsEnabled + WelcomeMailResendEnabled)
  // - pair this flag with it. ONE extra GET only on that error path.
  // Default (unset/"false") = today's behaviour: the generic error toast,
  // no refetch, the rejection. CI builds don't read .env.dev - set it in
  // the workflow yml.
  userCreateMailStatusEnabled:
    process.env['REACT_APP_USER_CREATE_MAIL_STATUS_ENABLED'] === 'true',
  // Flag REACT_APP_WELCOME_MAIL_DELIVERY_STATUS_ENABLED: the Onboarding
  // column (only read together with customerOnboardingEnabled) also shows
  // what SendGrid did with the welcome mail: delivered / opened (blue), NOT
  // delivered with the reason (red, e.g. "Template Render Failure"),
  // delayed (orange). Read off the three fields GET users?orgId already
  // carries with the API flag WelcomeMailDeliveryStatusEnabled
  // (welcomeMailDeliveryStatus / AtUtc / Reason; null = no event yet =
  // today's "Welcome mail sent"). 0 new calls per view. Default
  // (unset/"false") or the fields null = byte-identical DOM. CI builds
  // don't read .env.dev - set it in the workflow yml.
  welcomeMailDeliveryStatusEnabled:
    process.env['REACT_APP_WELCOME_MAIL_DELIVERY_STATUS_ENABLED'] === 'true',
  // Flag REACT_APP_MAIL_DELIVERY_ADMIN_ENABLED: the admin page "Mail
  // delivery" (menu item + route /admin/mail-delivery) - what SendGrid did
  // with the mails KitMaker sent (VELKOMSTMAIL-RENDER-FEJL-2026-10-01.md):
  // period 1/3/7/30 days, filters status + type, the totals, one row per
  // mail with the status and SendGrid's reason, "Resend welcome mail" per
  // row and "Resend all not delivered welcome mails" (dry run -> report ->
  // "Confirm and send"). PAIRED with the API flags
  // MailDeliveryAdminListEnabled (GET ops/mail-delivery) and
  // WelcomeMailResendBatchEnabled (POST ops/users/welcome-mail/
  // resend-batch); a 404 from either is said as "not active", never a
  // crash. Call budget: ONE GET when the page opens + ONE per filter change
  // / Refresh; ONE more GET (the welcome list, kind=welcome, 30 s cache)
  // only when a Status filter hides newer mails while a row offers a resend,
  // or when "Resend all" is opened; nothing polls and nothing is read
  // anywhere else. Default
  // (unset/"false") = no menu item, no route = byte-identical DOM and 0
  // calls. CI builds don't read .env.dev - set it in the workflow yml.
  mailDeliveryAdminEnabled:
    process.env['REACT_APP_MAIL_DELIVERY_ADMIN_ENABLED'] === 'true',
  // Flag REACT_APP_WELCOME_MAIL_SELF_SERVICE_ENABLED: on the admin page
  // "Mail delivery" (only read there, so only with mailDeliveryAdminEnabled)
  // the section "Resend welcome mail by user ID" and the dialog "Resend all
  // not delivered welcome mails" get the choice "Password: New temporary
  // password / User sets own password via "Forgot your password?"". The
  // choice is sent as passwordMode ("reset" | "selfService") on POST
  // ops/users/welcome-mail/resend-batch - on the dry runs too - and the
  // report shows the mode per user. selfService = the API resets NO
  // password (no Graph call) and the mail carries four steps instead of a
  // password. PAIRED with the API flag WelcomeMailSelfServicePasswordEnabled:
  // off there = 400 passwordModeNotEnabled (said in place, nothing sent);
  // a user whose dry-run answer does not name "selfService" (an API that
  // does not know the field would reset the password) is taken out of the
  // send. Call budget: no call of its own - the dry runs the page already
  // makes carry the mode; in "Resend all" a changed choice re-runs the dry
  // run (ONE call per 50 users). Default (unset/"false") = no choice, no
  // passwordMode on the wire = byte-identical DOM and wire. CI builds don't
  // read .env.dev - set it in the workflow yml.
  welcomeMailSelfServiceEnabled:
    process.env['REACT_APP_WELCOME_MAIL_SELF_SERVICE_ENABLED'] === 'true',
  // Feature flag: "Logos" section on the customer edit screen (design
  // kunde-onboarding 11/9-2026, FE part 1). When "true" a customer org (never
  // a vendor org) gets a Logos section: the logos the customer can see today
  // (GET logos?filterByField=customer.id, page size 50, fetched on the first
  // visit only), which of them are MANUAL links (GET
  // organizations/{orgId}/logos/attach), a search in the dealer's own
  // catalogue (GET logos?searchTerm=..., debounced 400 ms, min. 2 characters,
  // never prefetched) with multi-select and "Add to customer" (ONE POST
  // organizations/{orgId}/logos/attach { logoIds }), and "Remove" on the
  // manual links only (ONE DELETE organizations/{orgId}/logos/attach/{logoId}).
  // 0 NAV/apiPeter calls. Paired with the API flag CustomerLogoAttachEnabled
  // (with it off the attach routes answer 404 and the section shows the
  // customer's logos read-only with a "not active yet" note) - API first.
  // Default (unset/"false") = the section does not exist = byte-identical
  // DOM and wire. CI builds don't read .env.dev - set it in the workflow yml.
  customerLogosTabEnabled:
    process.env['REACT_APP_CUSTOMER_LOGOS_TAB_ENABLED'] === 'true',
  // Flag REACT_APP_CUSTOMER_LOGOS_TAB_V2_ENABLED (logo tab v2, 12/9-2026).
  // Requires REACT_APP_CUSTOMER_LOGOS_TAB_ENABLED (no section without it).
  // When "true" the customer's logo table grows from Image/No./Title/Actions
  // to Image · No. · Title · Size (mm) · Type · Colors · Method · Status ·
  // Source · Added · Actions - every extra column from the list row the
  // section already has (LogoDto) plus the link's source/added-by/removable
  // from the SAME GET organizations/{orgId}/logos/attach (which answers
  // CustomerLogoLinkDto[] once the API sub-flag CustomerLogoLinkSourceEnabled
  // is on, and int[] before that - both forms are normalised, so the FE
  // flag may go on in either order). "Remove" follows the link's source
  // (manual/assigned: today's confirm; linked = source unknown: confirm
  // with the "NAV's nightly sync may link it again" warning; the customer's
  // own upload / NAV's main customer / via parent: no button, a tooltip
  // explains), a 409 { code: "notRemovable", reason } from the DELETE is
  // toasted with the API's reason and the links are refetched, and "Open
  // logo" deep-links to the logo list on the item number. 0 new calls per
  // view (still 2 GET on the first visit). Default (unset/"false") = the
  // section of today, byte-identical DOM and wire. CI builds don't read
  // .env.dev - set it in the workflow yml.
  customerLogosTabV2Enabled:
    process.env['REACT_APP_CUSTOMER_LOGOS_TAB_V2_ENABLED'] === 'true',
  // Feature flag: cart price check (design CART-PRICE-CHECK 12/9; Nichlas:
  // "the moment something is put in the cart we check its price, without
  // it costing performance or speed"). The cart line is added AT ONCE with
  // the prices the page already knows - nothing waits, nothing blocks - and
  // afterwards ONE fire-and-forget POST organizations/{orgId}/logos/{logoId}/
  // price-check leaves per logo just added (a design = its logos, deduped,
  // at most 10; the same logo again within the API's 10 min cooldown = 0
  // calls). The API queues the serial and its worker runs ONE supplier
  // call per 3 s batch window; the request path never touches NAV or the
  // supplier. After a 202 queued the cart reads the light status GET on a
  // bounded 3/8/15 s chain (<= 3 calls) and, ONLY when the API says
  // "updated", re-reads that one cart entry (1 GetLogo / GetDesign through
  // useCartSync, which patches the line) and shows a discreet "Price
  // updated" note on the line when the price really changed. Silent on
  // every other outcome: no toast, 8 s timeout, errors to console.debug,
  // 404 (API flag off) = 5 min pause with one probe per window. Paired with
  // the API flag CartPriceCheckEnabled - API first. Default (unset/"false")
  // = byte-identical DOM and wire. CI builds don't read .env.dev - set it
  // in the workflow yml.
  cartPriceCheckEnabled:
    process.env['REACT_APP_CART_PRICE_CHECK_ENABLED'] === 'true',
  // Optional poll schedule for the status read above (only read when
  // cartPriceCheckEnabled is on): "3000,8000,15000" in ms, chained. Invalid
  // or unset = 3000,8000,15000; capped to 3 entries (owner budget, same
  // parser and cap as REACT_APP_HEAL_DELAYS_MS).
  cartPriceCheckPollMs: parseHealDelaysMs(
    process.env['REACT_APP_CART_PRICE_CHECK_POLL_MS'],
    DEFAULT_CART_PRICE_CHECK_POLL_MS
  ),
  // Flag REACT_APP_CART_PRICE_CHECK_LIST_SYNC_ENABLED ("the price follows to
  // the list", 12/9-2026). Requires REACT_APP_CART_PRICE_CHECK_ENABLED (no
  // list sync without the check). Nichlas 12/9: "when the price is updated
  // in the cart it is updated on the item too, when you get to the list" -
  // and "it must not cost performance or speed". When "true": every cart
  // detail that lands in useCartSync (the ONE GetLogo / GetDesign the check
  // already pays for, and any later landing of that cart entry - an
  // invalidation, the cart's Refresh, a new line; there is no periodic
  // re-read, refetchOnMount is off globally) has its PRICE fields copied
  // into the row with the same id in every cached list page of the current
  // act-as scope - a logo line into the logo list, the customer logo tab
  // and the infinite lists (the popup reads the very detail entry that
  // landed), a design line into the design list row (totalPrices/products
  // and the placed logos' rows INSIDE that row) - so the list the user
  // returns to, the popup and the cart's start price show the fresh price
  // with 0 calls. A design line never writes a placed logo's own logo-list
  // row or popup entry: GetDesign prices placed logos in the design's
  // vendor+customer NAV context, GetLogo/GetAllLogos in the caller's, and
  // the rows differ (verified in the API 12/9) - those refresh through the
  // logo's own GetLogo: the cart line's when it is also a logo line, else
  // ONE GetLogo the check fires on "updated" for a logo placed in design
  // lines only (useCartPriceCheck.landPlacedLogoInLists, 13/9) - the only
  // call this flag adds: <= 1 per placed logo, only when the API reported
  // its price updated, never on render, page or list view. Everything else
  // is pure in-memory react-query writes that keep each page's freshness
  // stamp (the pending-state patch too, under this flag): no invalidation,
  // no refetch, no seeding of absent entries. Default (unset/"false") =
  // byte-identical DOM, cache and wire. CI builds don't read .env.dev - set
  // it in the workflow yml.
  cartPriceCheckListSyncEnabled:
    process.env['REACT_APP_CART_PRICE_CHECK_ENABLED'] === 'true' &&
    process.env['REACT_APP_CART_PRICE_CHECK_LIST_SYNC_ENABLED'] === 'true',
  // Flag REACT_APP_CUSTOMER_LOGOS_TAB_FAST_ENABLED (logo tab fast, 12/9-2026).
  // Requires REACT_APP_CUSTOMER_LOGOS_TAB_ENABLED (no section without it).
  // Measured 12/9 in prod: the tab waited up to 24 s on GET logos
  // (customer.id filter, page 50) and 3 s on GET logos/attach - both fired
  // only on the first click of the section. When "true": (1) BOTH calls are
  // prefetched into the very query caches the section reads (same keys as
  // useAllLogos / useCustomerLogoLinks) 800 ms after the customer edit
  // screen's first paint and on hover of the "Logos" nav item - once per
  // customer, customer orgs only, never when the section was visited
  // already - so the click usually lands on warm data; (2) the customer's
  // page is 25 rows instead of 50 (half the hydration per page; the pager
  // is unchanged); (3) while the links are still in flight the v2 table
  // (which drew its rows as soon as the list landed already) reserves the
  // Source / Added / Actions cells with a light skeleton (Remove kept
  // invisible, not absent), so the rows do not jump when the links land.
  // Call budget per customer edit screen: +2 GET (the same 2 the first
  // click cost before, fired earlier; a failed prefetch is never retried)
  // - and those 2 GET are spent even when the section is never opened,
  // unless the mode below is "hover". Opening the section within 5 min of
  // the prefetch: 0 new calls (cache hit; the prefetch and the section's
  // two queries share a 5 min staleTime under this flag, mutations
  // invalidate explicitly); later than that the click refetches both = the
  // same 2 GET as today. A prefetched 404 (API flag off) is 1 GET + 1 GET
  // on the first visit (react-query never treats an error as fresh).
  // Default (unset/"false") = the section of today (60 s staleTime),
  // byte-identical DOM and wire. CI builds don't read .env.dev - set it in
  // the workflow yml.
  customerLogosTabFastEnabled:
    process.env['REACT_APP_CUSTOMER_LOGOS_TAB_FAST_ENABLED'] === 'true',
  // REACT_APP_CUSTOMER_LOGOS_TAB_PREFETCH_MODE: when the logos prefetch
  // above fires. "idle" (default, any other value) = 800 ms after first
  // paint AND on hover of the nav item; "hover" = on hover only (0 extra
  // calls for a customer screen where the section is never approached, at
  // the price of the hover-to-click gap being the only head start; the
  // hover is re-armed when the mounted screen switches to another org). Start
  // with idle; switch to hover if the telemetry (GetCustomerLogoAttachments
  // count vs. section openings) shows many unopened prefetches.
  customerLogosTabPrefetchMode:
    process.env['REACT_APP_CUSTOMER_LOGOS_TAB_PREFETCH_MODE'] === 'hover'
      ? ('hover' as const)
      : ('idle' as const),
  // Feature flag: the "Order shipped" switch on My profile > Notifications
  // becomes a real switch (checkbox enabled, badge NEW) instead of the
  // disabled COMING LATER row. The value it toggles (emailPreferenceOrder)
  // has always been round-tripped by the profile save, so 0 new calls.
  // PROD (binding): turn ON only when BOTH API flags are ON in prod -
  // OrderShippedEmailEnabled (the mail is actually sent) AND
  // EmailPreferenceMissingRowDefaultsTrueEnabled ("trin 0": GET profile
  // reports a missing row as true, like the mail gate does). Before trin 0
  // the API reports a missing row as false and the hooks' `?? false` shows
  // the switch OFF while the mail IS sent; the first save then writes false
  // and silently stops the mail. DEV: may run ahead of the API flags as a
  // pure UI click test - nothing is sent, and the save behaviour is exactly
  // today's (the disabled row already round-trips the value). Default
  // (unset/"false") = the disabled row of today, byte-identical DOM. CI
  // builds don't read .env.dev - set it in the workflow yml.
  emailPrefShippedEnabled:
    process.env['REACT_APP_EMAIL_PREF_SHIPPED_ENABLED'] === 'true',
  // Text-only companion of the flag above: with BOTH on, the "Order shipped"
  // description reads "...has been shipped or is ready for pickup"; with only
  // the shipped flag on it stays "...has been shipped." (the old text). Turn
  // ON only when the API flag OrderShippedEmailPickupEnabled is ON in the
  // same environment (the SendGrid pickup template exists) - otherwise the
  // UI promises pickup customers a mail that is not sent. Has no effect
  // while emailPrefShippedEnabled is off. 0 calls.
  emailPrefPickupEnabled:
    process.env['REACT_APP_EMAIL_PREF_PICKUP_ENABLED'] === 'true',
  // Feature flag: "Unassign customer" in the logo list's popup (design
  // LOGO-TAB-V2 12/9, B.2 - the counterpart of "Assign customer"). Shown
  // only for a logo that has a customer org, to an admin or vendor; a
  // confirm dialog warns that a link from NAV's customer filter comes back
  // with the nightly sync (the popup cannot tell the link's source without
  // an extra GET, so the warning is always shown); NAV's main customer
  // (mainOrganizationId = the customer) gets an explanatory line instead of
  // the button. The click is ONE DELETE organizations/{customerOrgId}/logos/
  // attach/{logoId} (the customer tab's existing route: 204 / 409
  // notRemovable / 404) - 0 calls per view. On 204 the popup closes, the
  // cached row and detail drop the customer at once (0 calls) and the logo
  // lists refetch (1 GET, the same as "Assign customer" today); the
  // customer tab's ["logo-links"] entry is invalidated (0 calls while it
  // is not on screen). Pair with the API flags CustomerLogoAttachEnabled
  // (the route) and CustomerLogoLinkSourceEnabled (without it only
  // tab-attached links can be removed; popup-assigned ones answer 404).
  // Default (unset/"false") = no button, byte-identical DOM and wire. CI
  // builds don't read .env.dev - set it in the workflow yml.
  logoUnassignEnabled:
    process.env['REACT_APP_LOGO_UNASSIGN_ENABLED'] === 'true',
  // Flag REACT_APP_UPLOAD_SINGLE_REQUEST (§3C, ANALYSE-UPLOAD-HASTIGHED
  // 13/9): a new image logo is created AND uploaded in ONE request. Today
  // the "Upload" click is two sequential requests - POST
  // organizations/image-logo (JSON {title, customerId}, ~0.5 s: host lead +
  // 3 INSERTs) then PUT organizations/{orgId}/image-logo/{logoId}
  // (multipart, which re-loads the empty draft with 10 SQL and re-runs
  // auth) - with an FE gap in between. When "true" the click is ONE
  // multipart POST organizations/{orgId}/image-logo (file, title,
  // customerId) that answers exactly the PUT's LogoDto; the react-query
  // cache is seeded from that answer precisely as from the PUT today (same
  // keys, same patches, 0 extra GetLogo), errors give today's toast and
  // the dialog stays open. Paired with the API flag
  // UploadSingleRequestEnabled: while that is off (or the API is not
  // deployed yet) the route answers 404 - without a body, or with the
  // repo's flag-off envelope { disabled, flag } - and the FE then falls
  // back to today's two requests for that upload and remembers it for 15
  // min in the tab (module memory, one 404 per tab per 15 min at most), so
  // this flag may be turned on BEFORE the API's. A 404 WITH a message (a
  // real "not found") is never a fallback - it is today's error toast.
  // Saves ~0.5-0.6 s per
  // upload; 2 requests -> 1, ~34 SQL -> ~15, supplier calls unchanged.
  // Default (unset/"false") = byte-identical wire: exactly today's two
  // requests, in today's order, with today's bodies. CI builds don't read
  // .env.dev - set it in the workflow yml.
  uploadSingleRequestEnabled:
    process.env['REACT_APP_UPLOAD_SINGLE_REQUEST'] === 'true',

  // Logo-list search feedback (customer report 14/9: "is it doing
  // anything?"): a spinner inside the search field from the first keystroke
  // (the 350 ms debounce window included) until the result has landed, a
  // status line under the header row ("Searching for '…'…" / "N logos match
  // '…'" / "No logos match '…'" + Clear search) and an indeterminate bar
  // above the dimmed previous rows while a new page loads. Built ONLY on the
  // react-query state the list already has (isFetching / isPreviousData /
  // totalCount): 0 extra calls; the debounce and the query key are
  // untouched. Default (unset/"false") = byte-identical DOM. CI builds don't
  // read .env.dev - set it in the workflow yml.
  logoSearchFeedbackEnabled:
    process.env['REACT_APP_LOGO_SEARCH_FEEDBACK_ENABLED'] === 'true',

  // Order list "Only show my designs" 500 (seen 9/9 and 14/9): the header's
  // showOnlyOwned checkbox is a FE-only key - the design and logo lists
  // translate it to mainOrganizationId before the API call, the API has no
  // such field - but ListHeader showed it on EVERY string-titled list with a
  // filter bar, the order list included, and the order list sent the key
  // straight to GET/POST organizations/orders ("'showOnlyOwned' is not a
  // member of type KitMaker.Model.Context.Order"). On: the checkbox is
  // opt-in per list (only the design list opts in) and the order list strips
  // the key from the header's filters AND from a URL/state that still
  // carries it (deep link, Back/Forward, bookmark) before any call. 0 extra
  // calls. Default (unset/"false") = byte-identical DOM and wire. CI builds
  // don't read .env.dev - set it in the workflow yml.
  ordersOwnedFilterFixEnabled:
    process.env['REACT_APP_ORDERS_OWNED_FILTER_FIX_ENABLED'] === 'true',

  // List search throttle (analysis 14/9 §3C, step 3). Prod today: every list
  // search box (TableSearchBar - the logo, design, order, customer, vendor,
  // manufacturer and style lists, the style pickers and the design editor's
  // logo panel) sends one call per typing pause > 350 ms with no minimum
  // length; 45 % of search->search pairs overlap and the server runs the
  // abandoned calls to completion. On: the field waits
  // REACT_APP_LIST_SEARCH_DEBOUNCE_MS (default 700) after the last
  // keystroke, sends nothing for a term shorter than
  // REACT_APP_LIST_SEARCH_MIN_CHARS (default 2) and shows "Type at least 2
  // characters" instead of the spinner; clearing the field still sends the
  // empty search, Enter still flushes at once. The rows (prices, stock,
  // status) are untouched - only WHEN a search is sent changes; 0 new
  // calls, fewer of today's. "Latest-only" needs no code: one react-query
  // key per term + keepPreviousData means an older term's answer can never
  // overwrite a newer term's rows (proved in listSearchLatestOnly.test).
  // Default (unset/"false") = 350 ms, no minimum, byte-identical DOM. CI
  // builds don't read .env.dev - set it in the workflow yml.
  listSearchThrottleEnabled:
    process.env['REACT_APP_LIST_SEARCH_THROTTLE_ENABLED'] === 'true',
  listSearchDebounceMs: parseListSearchDebounceMs(
    process.env['REACT_APP_LIST_SEARCH_DEBOUNCE_MS']
  ),
  listSearchMinChars: parseListSearchMinChars(
    process.env['REACT_APP_LIST_SEARCH_MIN_CHARS']
  ),
  // Design-list search feedback (colleague 14/9: "this must be on the design
  // page too"): the logo list's search feedback (logoSearchFeedbackEnabled)
  // on the design list - a spinner inside the search field from the first
  // keystroke (the 350 ms debounce window included) until the result has
  // landed, a status line under the header row ("Searching for '…'…" /
  // "N designs match '…'" / "No designs match '…'" + Clear search) and an
  // indeterminate bar above the dimmed previous rows while a new page loads.
  // Same shared building blocks (TableSearchBar activity, FunctionalList
  // props, useListSearchFeedback); built ONLY on the react-query state the
  // list already has (isFetching / isPreviousData / totalCount): 0 extra
  // calls; debounce, query key and the rows (prices, stock, status) are
  // untouched. Default (unset/"false") = byte-identical DOM. CI builds don't
  // read .env.dev - set it in the workflow yml.
  designSearchFeedbackEnabled:
    process.env['REACT_APP_DESIGN_SEARCH_FEEDBACK_ENABLED'] === 'true',
  // Supplier down = fail fast (straks-lindring 14/9). Prod 14/9 19:47 DK:
  // CreateAndUploadImageLogo ran 300 s and failed because app117's
  // Upload_Base64 hung; the user watched a spinning dialog for 5 minutes.
  // The same synchronous supplier call sits under clone, colour change,
  // method change and resize. PAIRED with the API's flag: while the
  // supplier is down the API answers those routes 503
  // { code: "supplierUnavailable", retryAfterSeconds } from its health
  // latch (0 supplier calls). On: errorStatusFromBody keeps the marker +
  // retryAfterSeconds, withToasts shows ONE clear toast ("The logo supplier
  // is not responding right now…", supplier_unavailable in all 10 locales)
  // instead of the compound "Could not …: <message>", the design editor's
  // extra "failed" warning is skipped for that error, the customer-list
  // upload dialog stays open with the file (as today) and the editor
  // re-opens its upload confirmation with the file kept, so one click
  // retries; every spinner/optimistic preview is rolled back by the
  // callers' own finally exactly as for any other error. 0 new calls.
  // Default (unset/"false") = byte-identical DOM and wire. CI builds don't
  // read .env.dev - set it in the workflow yml.
  supplierUnavailableEnabled:
    process.env['REACT_APP_SUPPLIER_UNAVAILABLE_ENABLED'] === 'true',
  // Client-side timeout (ms) for the two upload requests (POST
  // organizations/{orgId}/image-logo and PUT …/image-logo/{logoId}) via
  // AbortController - the safety net for the case where the API itself
  // does not answer (its own budget is shorter when its flag is on). Only
  // read when supplierUnavailableEnabled is on; an abort surfaces as the
  // same toast and the same kept file. Recommended 60000. Unset, empty,
  // non-numeric or <= 0 = no timeout (byte-identical wire); values under
  // 5000 are lifted to 5000 (a typo must never cancel every upload).
  uploadClientTimeoutMs: parseUploadClientTimeoutMs(
    process.env['REACT_APP_UPLOAD_CLIENT_TIMEOUT_MS']
  ),
  // Supplier-status banner (Nichlas 14/9: "a function that checks whether
  // the app117 functions work and, if not, tells the user about reduced
  // functionality" - BEFORE they try, not only as an error afterwards).
  // PAIRED with the API's flag SupplierStatusRouteEnabled: GET
  // supplier-status answers { ok, degraded, reason, since, retryAfterSeconds,
  // affected, lastProbeUtc, lastProbeOk, checkedUtc } from its health latch
  // (transient class only - never the deterministic per-item failures) and
  // the last App117Probe result, 0 supplier calls, cached in-process >= 30 s.
  // On: ONE GET after the profile has loaded, then a poll ONLY while a
  // logo/design editor or the logo list is mounted OR the status is
  // degraded (REACT_APP_SUPPLIER_STATUS_POLL_MS, default 60 000, ymls
  // 420 000 = ~7 min since 15/9; 0 calls on other pages, none while the tab
  // is hidden), one immediate re-check after a 503 supplierUnavailable; a
  // 404 from the route (API flag off) is a silent "ok" and stops the poll
  // for the session. Degraded = an amber, non-blocking bar under the header
  // ("Reduced functionality: the logo supplier is not responding right
  // now…", supplier_status_banner in all 10 locales) with the episode's
  // relative start time, a countdown to the next read ("Next check in
  // m:ss", 15/9) and a "Check now" button (one extra GET, locked 15 s),
  // dismissable per session, back on the next state change. Default (unset/"false") =
  // byte-identical DOM and wire (no hook, no GET). CI builds don't read
  // .env.dev - set it in the workflow yml.
  supplierStatusEnabled:
    process.env['REACT_APP_SUPPLIER_STATUS_ENABLED'] === 'true',
  // Poll interval (ms) of the status read while polling is on (see above):
  // the next read is due this long after the last answer, and the banner
  // counts down to it. Unset, empty, non-numeric or <= 0 = 60 000; clamped
  // to 15 000-600 000. The ymls set 420 000 (~7 min, Nichlas 15/9): the
  // API's probe runs every 5 min, so healing is seen within <= 7 min. Only
  // read with the flag above on.
  supplierStatusPollMs: parseSupplierStatusPollMs(
    process.env['REACT_APP_SUPPLIER_STATUS_POLL_MS']
  ),
  // Feature flag: admin page "NAV price fallback" (Nichlas 15/9: "a list in
  // admin ... per customer count, expandable, and pull a CSV file so we can
  // fix them over time"). Lists the logos whose price could not be fetched
  // from the supplier ("Error price") and were priced from NAV's own sales
  // price instead - one row per customer with the serial count, expandable
  // to the serials, and a CSV export (all customers or one). Call budget:
  // ONE GET ops/nav-price-fallback/summary on page open (5 min stale, no
  // polling, no retries), ONE GET ops/nav-price-fallback/customers/{orgId}
  // per expanded customer (cached, never refetched on collapse/expand), the
  // CSV only on click; Refresh re-reads the summary + the expanded
  // customers; "Fetch NAV prices again" = ONE POST
  // ops/nav-price-fallback/refresh per click (all, or one customer), then
  // the same re-read once. No call on any other page. Paired with the API flag
  // NavSalesPriceFallbackEnabled - API first (with it off the routes answer
  // 404 { disabled: true } and the page shows "not active"). Default
  // (unset/"false") = byte-identical to today: no menu item, no route, no
  // calls. CI builds don't read .env.dev - set it in the workflow yml.
  navPriceFallbackAdminEnabled:
    process.env['REACT_APP_NAV_PRICE_FALLBACK_ADMIN_ENABLED'] === 'true',
  // Feature flag (re-review of the customer-owned uploads, 16/9): after a
  // successful style upload the new style is PREPENDED into the cached style
  // lists (the open chooser's infinite query + the design list's paged
  // query, same act-as scope, unfiltered lists only) and every ["styles"]
  // entry is marked stale WITHOUT refetching (refetchType "none") - so the
  // dialog closes as soon as the POST answers and other screens revalidate
  // lazily on their next mount. Default (unset/"false") = today's behaviour:
  // refetchQueries over EVERY cached ["styles"] entry (one GET per loaded
  // chooser page + the design list + style details) and the spinner waits
  // for all of them. CI builds don't read .env.dev - set it in the workflow yml.
  uploadListPatchEnabled:
    process.env['REACT_APP_UPLOAD_LIST_PATCH_ENABLED'] === 'true',
  // Feature flag (same re-review): hardening of the "Upload your own images"
  // dialog. (1) The dialog is only MOUNTED while open, so its state (brand,
  // model, images, calibration) is reset on every close - reopening after a
  // success no longer offers a pre-filled form whose one click creates a
  // duplicate style - and the left/right preview blob URLs are revoked on
  // unmount. (2) GET /styles/categories is fetched lazily on the first open
  // and cached for the session (react-query, staleTime Infinity) instead of
  // on every mount of Design Create / Change Style; a failed read shows a
  // toast and is retried on the next open. (3) The upload zones reject
  // files that can never be accepted (type/extension, drag-and-drop
  // included) before the upload instead of after it - the accepted set is
  // the API sniffer's (PNG, JPEG, GIF, BMP, WebP, HEIC/HEIF; a generic
  // type such as application/octet-stream falls back to the extension) and
  // the <input accept> hint follows the same set incl. the .heic/.heif
  // extensions. (4) When the API's duplicate guard
  // (StyleUploadDuplicateGuardEnabled: same owner + brand + model + views)
  // answers an upload with the EXISTING style, the toast says the existing
  // style was reused instead of "created" (createdAtUtc older than the
  // request by more than 5 min). Default (unset/"false") = byte-identical
  // to today. CI builds don't read .env.dev - set it in the workflow yml.
  uploadDialogHardeningEnabled:
    process.env['REACT_APP_UPLOAD_DIALOG_HARDENING_ENABLED'] === 'true',
  // Feature flag (same re-review): the API's ChangeStyleAsync only accepts
  // Draft designs (400 "Design status must be 'Draft'"), but the editor's
  // "Change style" button was only disabled for Done designs - an Approval /
  // Rejected design could open the chooser and only fail on Continue. On:
  // the button is disabled (with a tooltip) for every non-Draft status, and
  // the Change Style screen itself shows a notice and disables Continue when
  // it is reached for a non-Draft design (deep link / back navigation).
  // Default (unset/"false") = byte-identical to today.
  changeStyleDraftOnlyEnabled:
    process.env['REACT_APP_CHANGE_STYLE_DRAFT_ONLY_ENABLED'] === 'true',
  // Feature flag (follow-up to the re-review, 16/9): after Change Style, a
  // warning toast shows how many logos the API (flag
  // StyleChangeLogoRelocationEnabled) moved to the first view because their
  // view does not exist on the new style (DesignDto.relocatedLogoCount).
  // Own flag so the toast can be rolled back on its own - it used to ride
  // on REACT_APP_UPLOAD_DIALOG_HARDENING_ENABLED, which has nothing to do
  // with Change Style. No call of its own (the count is on the PUT answer
  // already). Default (unset/"false") = no toast. CI builds don't read
  // .env.dev - set it in the workflow yml.
  changeStyleRelocatedToastEnabled:
    process.env['REACT_APP_CHANGE_STYLE_RELOCATED_TOAST_ENABLED'] === 'true',
  // Feature flag (Nichlas 17/9): admin page "Mail test" (admin/mail-test) -
  // send any KitMaker e-mail template to an address of our own, rendered
  // with a chosen vendor's own logos/designs/orders/users, or preview the
  // exact dynamic_template_data as JSON without sending. Call budget: ONE
  // GET ops/mail-test/templates on page open, ONE GET ops/mail-test/samples
  // per chosen vendor (both 5 min stale, no retry, no polling), ONE POST
  // ops/mail-test/send per "Preview data" / "Send test mail" click; 0 calls
  // anywhere else. Paired with the API flag MailTestSendEnabled - API first
  // (with it off the routes answer 404 and the page shows "not active").
  // Default (unset/"false") = byte-identical to today: no menu item, no
  // route, no calls. CI builds don't read .env.dev - set it in the workflow yml.
  mailTestEnabled: process.env['REACT_APP_MAIL_TEST_ENABLED'] === 'true',
  // Feature flag (Nichlas 20/9, "let me put my own designs/logos into the
  // mail test so we can run EVERY test"): three additions on top of
  // mailTestEnabled, all behind this one flag.
  //  (A) "Find items" on the Mail test page: search ANY logo / design under
  //      the vendor's org tree (serial, MUL, name, customer; pending only or
  //      all; max 50) instead of the 15 newest samples, tick the ones to use
  //      - they flow through the same sample selection, so the existing
  //      "Preview data" / "Send test mail" (isTest) work with them.
  //  (B) "Real run": the chosen items through the REAL approval-mail path
  //      (the wake queue + worker of the JS upload / NAV import and of the
  //      vendor -> customer mail): bundling, allowlist, per-org settings,
  //      daily cap, minimum spacing and the approval-mail log apply exactly
  //      as in production - Preview (dry run) shows the accepted items and
  //      the real recipient chain, "Run enqueuer" queues the wake, "Send
  //      now" skips only the bundle window. Afterwards the page polls the
  //      recipient org's approval-mail log for the new row.
  //  (C) "Run reminders" on the admin page "Approval mails": the nightly
  //      reminder sweep on demand (dry run with a movable date, or for real
  //      after a confirmation), plus ?orgId= on that page as a deep link
  //      from a real run's result.
  // Call budget: ONE GET ops/mail-test/items per settled search (300 ms
  // after typing stops, or Search/Enter; a repeated search is a cache hit
  // for 5 min), ONE POST ops/mail-test/approval-run per Preview / Run click
  // (a second POST only after the admin confirms the empty-allowlist
  // dialog), at most 12 GET organizations/{org}/approval-mail/log 5 s apart
  // after a real run (stops at the first new row), ONE POST
  // ops/approval-mail/reminders/run per "Run reminders" click (+ ONE stats
  // GET after a real reminder run so the tables follow). 0 calls anywhere
  // else, 0 NAV/apiPeter. Paired with the API flag
  // MailTestApprovalRunEnabled - API first: the templates catalogue says
  // approvalRunEnabled and the page hides everything above while it is off
  // (its routes answer 404). Default (unset/"false") = byte-identical: no
  // finder, no panel, no reminder block, ?orgId ignored, no calls. CI
  // builds don't read .env.dev - set it in the workflow yml.
  mailTestApprovalRunEnabled:
    process.env['REACT_APP_MAIL_TEST_APPROVAL_RUN_ENABLED'] === 'true',
  // Feature flag (Nichlas 18/9, customer case "Kroppedal Museum": item
  // 1095073 came out BLURRED in a vendor proof PDF): a proof PDF may never
  // silently contain a low-resolution logo. A brand-new imported logo has no
  // stored 1000 px image until someone opens it; the first GetLogo from the
  // PDF flow answers without a high-res and queues the render, the old ONE
  // 5 s retry gave up, and the 60x68 px thumbnail was scaled to ~2000 px.
  // When "true", every PDF path in pdf-generator.service.ts (single logo,
  // logo list, designs, public proof screens):
  //  - judges each IMAGE logo with the shared sharpness rule (pdfSharpGate:
  //    displayImage.highResPending, highResUrl missing, highResUrl ===
  //    lowResUrl, or natural longest side < 400 px - so it also works
  //    against an API that does not send highResPending yet),
  //  - waits BOUNDED for the queued render (REACT_APP_PDF_SHARP_WAIT_MS) and
  //    re-fetches ONLY the unsharp logos with GetLogo?purpose=proof, while
  //    the loading toast says "Preparing sharp logo images… (2/4)",
  //  - and if a logo is still not sharp STOPS before anything is saved: a
  //    dialog lists the logos with "Try again" (a new user action = a new
  //    budget), "Download anyway" (0 new calls, then a warning toast) and
  //    "Cancel".
  // Call budget (owner rule: at most 3 GetLogo per logo per action): a warm
  // logo = 1 GetLogo exactly as today; a cold logo = 1 + at most 2
  // re-fetches = 3. The public screens repeat their ONE batch call at most
  // twice, and only while something is unsharp. 0 calls on page views.
  // Paired with the API flag ProofHighResPendingEnabled (queue reason
  // "proof" bypasses the background gate, so the render lands in seconds) -
  // the FE flag works alone, but then "Try again" is needed more often.
  // Default (unset/"false") = byte-identical: the old single 5 s retry, no
  // purpose parameter, no gate, no dialog. CI builds don't read .env.dev -
  // set it in the workflow yml.
  pdfWaitSharpEnabled: process.env['REACT_APP_PDF_WAIT_SHARP_ENABLED'] === 'true',
  // Optional wait schedule for the sharp wait above (only read when
  // pdfWaitSharpEnabled is on): "6000,24000" in ms = the waits before
  // re-fetch 1 and 2. Invalid or unset = 6000,24000; capped to 2 entries so
  // the budget of 3 GetLogo per logo can never be widened from the yml.
  pdfSharpWaitDelaysMs: parsePdfSharpWaitMs(
    process.env['REACT_APP_PDF_SHARP_WAIT_MS']
  ),
  // Feature flag (Nichlas 19/9, design APPROVAL-REMINDERS): "Approval
  // e-mails" section on the org edit screen (customer AND vendor orgs). Per
  // organisation: whether "ready for approval" e-mails are sent TO it
  // (on/off), one e-mail per item or one combined e-mail (vendor orgs only,
  // where the API says modeSupported), how many reminders (0-3) and the days
  // between them (1-14) - saved by the screen's ONE footer Save (their own
  // PUT, fired by that same click; the footer's unsaved-changes indicator
  // covers them) - plus the log of what was sent and reminded (per mail,
  // expandable to the items, filterable per item). On a CUSTOMER org the
  // settings govern the mails the customer receives (direction 2), set by
  // the vendor/admin from the customer page; on a VENDOR org they govern the
  // mails the vendor receives (JS upload/NAV + customer-submitted), reachable
  // by an admin only through the org edit screen. Call budget: 1 GET
  // settings + 1 GET log (top 200) the first time the section is opened
  // (5 min stale, no retry, no polling), Save = 1 PUT with the changed
  // fields only, its answer written into the cache (0 GET; an empty answer
  // from an older API = 1 refetch), 1 GET per applied log filter; 0 calls
  // on every other section, 0 NAV/apiPeter.
  // Paired with the API flags ApprovalMailPerOrgSettingsEnabled (settings
  // routes) and ApprovalMailLogEnabled (log route; SQL first) - API first:
  // while they are off the routes answer 404 and the section says "not
  // active yet" (a PUT 404 toasts and the fields snap back). Default
  // (unset/"false") = the section does not exist = byte-identical DOM and
  // wire. CI builds don't read .env.dev - set it in the workflow yml.
  approvalMailSettingsEnabled:
    process.env['REACT_APP_APPROVAL_MAIL_SETTINGS_ENABLED'] === 'true',
  // Feature flag (Nichlas 19/9, design APPROVAL-REMINDERS round 2): admin
  // page "Approval mails" (admin/approval-mails) - WHEN and TO WHOM approval
  // e-mails were sent: a period picker (two dates, last 7/30/90 days, an
  // optional org id), a table per Danish day (sent, first, reminder 1-3,
  // closed, not sent because of the test allowlist; a day expands to
  // direction x source), the top recipients (org name + id linking to the
  // org edit screen's "Approval e-mails" section, e-mail, mails, reminders,
  // not sent, items, last) and the status NOW of the mailed items (awaiting
  // / approved / rejected / deleted / not found). A banner says whether the
  // API's test allowlist is active and which entries it holds. Call budget:
  // ONE GET ops/approval-mail/stats on page open and ONE per "Apply" (60 s
  // stale, no retry, no polling); 0 calls anywhere else, 0 NAV/apiPeter
  // (the API answers from 4 SQL). Paired with the API flag
  // ApprovalMailLogEnabled (SQL first) - API first: while it is off the
  // route answers 404 and the page says "not active yet". Independent of
  // approvalMailSettingsEnabled above (the org link lands on Details while
  // that section does not exist). Default (unset/"false") = no menu item,
  // no route, no calls = byte-identical. CI builds don't read .env.dev - set
  // it in the workflow yml.
  approvalMailStatsEnabled:
    process.env['REACT_APP_APPROVAL_MAIL_STATS_ENABLED'] === 'true',
  // Feature flag (Nichlas 20/9, design APPROVAL-MAIL-SELFSERVICE): vendor
  // self-service of approval e-mails on My profile (vendor users) - a sixth
  // nav item "Approval e-mails" with (B) a PERSONAL block "My approval
  // e-mails" (whether THIS user receives the "ready for approval" e-mails
  // and reminders sent to the organisation: follow the organisation /
  // always / never; null on the wire = follow = today's behaviour) and (A)
  // the organisation's own "Approval e-mails" block (the SAME section and
  // hook as the admin's org edit screen, with the caller's own org id;
  // editable for the main branch / a branch with EditAllowed, read-only
  // otherwise) with the sent log. Call budget: 1 GET profile/approval-mail-
  // preferences + 1 GET settings + 1 GET log the first time the section is
  // opened (5 min stale, no retry, no polling); Save = ONLY the dirty
  // PUTs (a settings-only Save never re-PUTs the profile/org/commission);
  // 0 calls on every other section, 0 NAV/apiPeter. The personal block
  // needs this flag only (API ApprovalMailSelfServiceEnabled, 404 while
  // off = "not active yet"); the org block needs approvalMailSettingsEnabled
  // above as well (same API flags as the admin page). Hidden under admin
  // act-as (the token is the admin's, not the vendor's). Default
  // (unset/"false") = the nav item does not exist = byte-identical DOM and
  // wire. CI builds don't read .env.dev - set it in the workflow yml.
  approvalMailSelfServiceEnabled:
    process.env['REACT_APP_APPROVAL_MAIL_SELFSERVICE_ENABLED'] === 'true',
  // --- Clarity ---
  // Feature flag (design DESIGN-CLARITY 22/9, PR A): consent before any
  // analytics tag. With "true" public/index.html loads NEITHER the Clarity
  // tag NOR gtag.js until the stored choice (localStorage
  // kitmaker.consent.v1, max 270 days old) is "granted", never on /public*
  // or /approval* (approval keys travel in those URLs); the consent banner
  // after login, "Cookie settings" in the user menu and footer, the route
  // guard and the cookie deletion on Decline exist only with it. The
  // placeholder in index.html must also be set in the workflow yml. Off =
  // today's behaviour: tags load unconditionally, no banner, no consent
  // signals.
  clarityConsentEnabled:
    process.env['REACT_APP_CLARITY_CONSENT_ENABLED'] === 'true',
  // Feature flag: the Clarity facade (src/utils/analytics/clarity.ts) -
  // custom tags rolle/forhandler/kunde/sprog/km_session and identify
  // (hashed B2C object id) re-sent on every route change, stop for internal
  // users, crossorigin on the vendor scripts. Never "true" in prod without
  // clarityConsentEnabled (identify without consent). Default (unset/
  // "false") = byte-identical. CI builds don't read .env.dev - set it in
  // the workflow yml.
  clarityEnabled: process.env['REACT_APP_CLARITY_ENABLED'] === 'true',
  // Feature flag: the eight Clarity events (logo_uploadet, logo_oprettet,
  // design_gemt, tilfoej_til_ordre, excel_upload, kurv_fortsaet,
  // ordre_afgivet, fejl_vist). Requires clarityEnabled as well. Default
  // (unset/"false") = no event call anywhere.
  clarityEventsEnabled:
    process.env['REACT_APP_CLARITY_EVENTS_ENABLED'] === 'true',
  // Feature flag: record internal users (admin, act-as, e-mails in the
  // internal domains below). "false" = the facade calls clarity('stop') for
  // them and sets neither tags nor identify. Dev turns it on so testers
  // see their own sessions. Prod: internal users are never recorded.
  clarityRecordInternal:
    process.env['REACT_APP_CLARITY_RECORD_INTERNAL'] === 'true',
  // Feature flag: Microsoft's cookieless model - load the tags BEFORE
  // consent with analytics_storage denied. Placeholder in index.html only.
  // Requires written legal sign-off and a changed banner text before it is
  // ever set to true.
  analyticsLoadBeforeConsent:
    process.env['REACT_APP_ANALYTICS_LOAD_BEFORE_CONSENT'] === 'true',
  // E-mail domains that mark a user as internal for Clarity (list, not a
  // flag; split/trim/lowercase like apiKeysAdminEmails). The e-mail is read
  // only in the browser to a boolean and never leaves the client.
  clarityInternalEmailDomains: (
    process.env['REACT_APP_CLARITY_INTERNAL_EMAIL_DOMAINS'] ??
    'jetsport.dk,id-unlimited.dk'
  )
    .split(',')
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean),
  // Build values (public ids, not flags): the Clarity project id (admin
  // statistics link; empty = the link is hidden) and the GA4 measurement id
  // (only for deleting the _ga_<id> cookie on Decline). CI sets both in the
  // workflow yml - dev gets its own Clarity project, prod the prod ids.
  clarityProjectId: process.env['REACT_APP_CLARITY_PROJECT_ID'] ?? '',
  ga4MeasurementId: process.env['REACT_APP_GA4_MEASUREMENT_ID'] ?? '',
  // URL of the privacy policy linked from the consent banner/dialog.
  // REQUIRED for the banner: without it the banner is not rendered (one
  // console.warn) and therefore no analytics is ever loaded with the
  // consent flag on - consent without accessible information is invalid.
  privacyPolicyUrl: process.env['REACT_APP_PRIVACY_POLICY_URL'] ?? '',
  // --- UX – døde klik (Clarity 21/9) ---
  // Three feature flags (Nichlas' Clarity report 21/9, design DESIGN-CLARITY
  // §6): dead clicks on /order/place, the header cart, /order/confirm and the
  // logo/design lists. Every fix is classes, handlers and markup gated in JSX
  // - no new calls, no logic change behind a flag. Default (unset/"false") =
  // byte-identical DOM. CI builds don't read .env.dev - set them in the
  // workflow yml.
  // /order/place + header cart: empty "Placements/Sizes/Qty" panel becomes a
  // "click to add the first row" button, row labels lose their input look,
  // trash/chevron get cursor + title, the cart button scrolls to the top
  // (pulse) instead of re-navigating when already on /order/place, and the
  // admin queue box opens /admin/import-status.
  uxCartFixesEnabled: process.env['REACT_APP_UX_CART_FIXES_ENABLED'] === 'true',
  // /order/confirm (classic form AND card section): delivery-type / packaging
  // / GLS rows become radios with cursor + hover, a placed order says why the
  // options cannot be changed, the order-line image enlarges on click,
  // "Choose from previous addresses" is a real button, an empty address book
  // says so, and labels focus their field.
  uxConfirmFixesEnabled:
    process.env['REACT_APP_UX_CONFIRM_FIXES_ENABLED'] === 'true',
  // /logo + /design: the search frame focuses the field and the magnifier
  // starts the search, "Move selected" / "Add to order" without a selection
  // explain themselves (tooltip + toast, no "(0)"), row text no longer
  // swallows the row click, rows show a pointer.
  uxListFixesEnabled: process.env['REACT_APP_UX_LIST_FIXES_ENABLED'] === 'true',
  // Dead clicks round 2 (Clarity analysis 26/9, PR D): the "Type at least N
  // characters" hint in the search frame is a <label> for the field and the
  // same text in the status line under the list header is a button that
  // focuses the field (one window event, no prop drilling); an order row
  // says "Open order" (title, focusable, Enter/Space) and a hidden-price
  // customer gets a toast instead of silence; the dimmed list shows a
  // progress cursor while it re-fetches; the supplier banner carries a hint
  // title. Handlers, attributes and one toast only - 0 new calls. The
  // masking attributes (data-clarity-mask/unmask, clarityAttrs.ts) are NOT
  // behind this flag (design DESIGN-CLARITY section 8.1). Default (unset/
  // "false") = byte-identical DOM. CI builds don't read .env.dev - set it
  // in the workflow yml.
  clarityUx2Enabled: process.env['REACT_APP_CLARITY_UX2_ENABLED'] === 'true',
  // --- Design create: require org (23/9, OVERDRAGELSE-2026-09-23 §7 B) ---
  // Prod fact: an Admin without a picked vendor (and without a profile org)
  // sends POST /organizations/null/designs - the API route binds
  // {orgId:required} without :int, so the request dies as a binding error
  // = 500 instead of 404 (20/8 x2, 23/9 x2; no design is created). With the
  // flag on the create screen blocks Continue for that case (hint + a link
  // that focuses the vendor field, toast on a forced submit) and
  // ApiClient.createDesign refuses locally with the same message before any
  // URL is built. Off = byte-identical DOM and wire. CI builds don't read
  // .env.dev - set it in the workflow yml.
  designCreateRequireOrgEnabled:
    process.env['REACT_APP_DESIGN_CREATE_REQUIRE_ORG'] === 'true',
  // --- Oversize sheet guard (24/9, DESIGN-OVERSIZE-SHEET-GUARD) ---
  // For the vendors the API lists (OversizeLogoSheetVendorIds) an IMAGE logo
  // wider OR taller than the API's limit (300 mm) is not put in the cart:
  // a dialog says the sheet size is no longer available and offers ONE
  // "Request new sheet size" click = ONE POST organizations/{orgId}/logos/
  // {logoId}/sheet-size-request (the API mails ID UNLIMITED's Zendesk inbox).
  // The hint (enabled / maxMm / applies / requestEnabled) rides on the
  // already prefetched GET logos/options/all: 0 new calls on any view, the
  // add-to-cart check is a pure predicate. The API independently refuses
  // CommitOrder/PlaceOrder with 409 sheetSizeUnavailable, which the same
  // dialog shows (context "place"; withToasts skips the compound toast).
  // Paired API flag OversizeLogoSheetGuardEnabled - API first. Default off
  // = byte-identical DOM and wire (the hint is ignored, mapError and
  // withToasts are untouched). CI builds don't read .env.dev - set it in
  // the workflow yml.
  oversizeSheetGuardEnabled:
    process.env['REACT_APP_OVERSIZE_SHEET_GUARD_ENABLED'] === 'true',
  // --- Oversize sheet guard: admin act-as (27/9) ---
  // Under act-as the session's role is the impersonated vendor's (GET
  // profile/{orgId} answers role Vendor), so the admin branch of
  // resolveOversizeApplies never ran and an admin acting as a listed vendor
  // could put a large logo in the cart (only the API's 409 at commit/place
  // stopped it). On: an act-as org found in the hint's vendorIds activates
  // the guard whatever the profile role says - safe, because the API sends
  // vendorIds ONLY to admins and act-as state is admin-only. 0 new calls.
  // Default off = today's behaviour, byte-identical. CI builds don't read
  // .env.dev - set it in the workflow yml.
  oversizeSheetActAsGuardEnabled:
    process.env['REACT_APP_OVERSIZE_SHEET_ACT_AS_GUARD_ENABLED'] === 'true',
  // --- Oversize sheet guard: vendors managed from admin (25/9,
  // DESIGN-OVERSIZE-ADMIN-VENDORS) ---
  // Admin-only checkbox "Sheet size guard (large logos)" on the vendor edit
  // screen (/vendor/:id/edit, Details section): ONE immediate
  // PUT ops/organizations/{orgId}/oversize-sheet-vendor {enabled} (like
  // "Allow own branding" - not part of the footer Save, which is ERP-locked
  // on the vendor form). The checkbox shows the admin (DB) part of the
  // vendor list; a badge says when the vendor is ALSO listed in the API's
  // app setting. The section renders nothing when the org detail lacks
  // oversizeSheetVendorEnabled (API flag OversizeLogoSheetAdminVendorsEnabled
  // off / older API), so this flag may be on before the API's. 0 new calls
  // on any view: maxMm in the help text is read from the already cached
  // logo-options hint (never a new query). Default off = byte-identical DOM
  // and wire. CI builds don't read .env.dev - set it in the workflow yml.
  oversizeSheetAdminEnabled:
    process.env['REACT_APP_OVERSIZE_SHEET_ADMIN_ENABLED'] === 'true',
  // --- Oversize sheet guard: admin page "Sheet size" (26/9) ---
  // Admin menu item + route admin/sheet-size: ALL vendors in one list
  // (name, NAV no., org id, customers) with a checkbox per vendor = the SAME
  // PUT ops/organizations/{orgId}/oversize-sheet-vendor as the vendor edit
  // screen's checkbox, through the same repository function, so both places
  // show the same state at once (the PUT's answer is written into the cached
  // list; the org detail is refetched as today). Vendors also listed by the
  // API's app setting show enabled and locked with a badge. Data = ONE GET
  // organizations/Vendors?pageSize=0 (EVERY vendor, own cache key - not the
  // paged 25-row start-up prefetch) + ONE GET ops/oversize-sheet-vendors per
  // visit;
  // ONE PUT per click and nothing else on the wire; Refresh refetches both;
  // no polling. Default off = no route, no menu item, 0 calls =
  // byte-identical. CI builds don't read .env.dev - set it in the workflow
  // yml.
  oversizeSheetAdminPageEnabled:
    process.env['REACT_APP_OVERSIZE_SHEET_ADMIN_PAGE_ENABLED'] === 'true',
  // --- Oversize sheet guard: "Logos > 30 cm" column on the admin page
  // "Sheet size" (27/9) ---
  // Per vendor: how many IMAGE logos of the vendor and its customers have a
  // side over the guard's limit (the API's OversizeLogoSheetMaxMm, 300 mm),
  // counted by the API with the guard's own rule (GET
  // ops/oversize-sheet-vendors/logo-counts, API flag
  // OversizeLogoSheetCountsEnabled, 120 s server cache). ONE GET per visit
  // (120 s client staleTime); Refresh refetches it; a 404 (API flag off /
  // older API) silently omits the column - no toast. The column header
  // sorts the list client-side (first click = most first, second =
  // fewest first; ties by name). Default off = no column, 0 calls =
  // byte-identical. CI builds don't read .env.dev - set it in the workflow
  // yml.
  oversizeSheetAdminCountsEnabled:
    process.env['REACT_APP_OVERSIZE_SHEET_ADMIN_COUNTS_ENABLED'] === 'true',
  // --- Oversize sheet guard: inline request instead of add-to-order (28/9) ---
  // Nichlas 28/9: "der må ikke være knappen men i stedet det med forespørg
  // ny design". While the guard is ACTIVE for the session and an image logo
  // is over the hint's limit (isOversizeLogo with maxMm + bothSides), the
  // quantity stepper + "Add to order" on the logo list row and the logo
  // details page are replaced by "Request new sheet size" (the SAME POST
  // organizations/{orgId}/logos/{logoId}/sheet-size-request as the dialog,
  // only on click) with "Request sent" from the same per-viewer
  // sessionStorage memory (TTL from the hint); hidden when the hint says
  // requestEnabled=false. LOGOS ONLY - designs never (Nichlas 28/9: "Design
  // skal ikke påvirkes af dette"): the design list/details keep today's
  // stepper. The cart hole: a standalone oversize logo line already in the
  // cart (restored bucket, quantity change) is marked on the cart strip,
  // its line view shows the same request, and Continue is stopped (dialog
  // "place"); an extra copy in the heating-fee dialog (a new standalone
  // line, also of a design's own logo) shows the request instead of the
  // stepper and is stopped before the draft (dialog "add"). 0 new calls
  // (the hint rides on the cached logos/options/all). The dialog and the
  // API's 409 stay as the safety
  // net. Requires REACT_APP_OVERSIZE_SHEET_GUARD_ENABLED. Default off =
  // byte-identical DOM and wire. CI builds don't read .env.dev - set it in
  // the workflow yml.
  oversizeSheetInlineRequestEnabled:
    process.env['REACT_APP_OVERSIZE_SHEET_INLINE_REQUEST_ENABLED'] === 'true',
  // --- Cart storage v2 (24/9, DESIGN-CART-STORAGE-2026-09-24 rev. 2) ---
  // Prod fact (customer Hoyrup, 131 lines / 4 designs, 45 name rows on one
  // design): every persisted design line carries a FULL BaseLogo copy per
  // instance logo (45 x 2 = 90 identical copies = 91 % of a 649 KB line),
  // the localStorage quota is hit, every 400 ms persist fails and every
  // failure stacks another "Storage quota exceeded" toast - nothing is saved
  // and a reload loses the edits. With the flag on, CartContext persists
  // through utils/cartStorage: instance logos are stored as references to
  // the design's own designLogos[].logo (rehydrated on read, existing copy
  // first), Organization copies are reduced to the six fields a cart reader
  // uses, and a quota failure shows ONE toast per episode with a console
  // diagnostic. Same key, same root type (CartItem[]), no envelope: a v2
  // bucket is a valid v1 bucket, so the flag can be turned off again without
  // any migration. Off = byte-identical code path and storage format. CI
  // builds don't read .env.dev - set it in the workflow yml.
  cartStorageV2Enabled:
    process.env['REACT_APP_CART_STORAGE_V2_ENABLED'] === 'true',
  // --- Cart storage fallback (24/9, DESIGN-CART-STORAGE-TRIN3-2026-09-24) ---
  // Step 3 of the cart storage work: when a localStorage write STILL hits
  // the quota under v2, the cart no longer lives in memory only. Layer 1
  // re-slims this user's own PARKED v1 buckets (other act-as identities)
  // once per session and retries the write; layer 2 keeps a write-through
  // shadow copy of the same v2 string in IndexedDB (kitmaker-cart-shadow),
  // gated by a ~35-char marker key `<bucket>_shadow` = `fingerprint|nonce`
  // in localStorage. IndexedDB is read ONLY when that marker exists and its
  // fingerprint matches the bucket (0 IndexedDB calls and no database for
  // users who never hit the quota; +1 getItem per mount). Every bucket
  // deletion (empty cart, clear, Stripe persistNow, "Reset cart") removes
  // the marker synchronously, so an ordered cart can never come back. The
  // quota toast is shown only when BOTH layers fail. Requires
  // cartStorageV2Enabled; off (or v2 off) = byte-identical v2 code path,
  // markers and shadows are ignored. 0 HTTP calls. CI builds don't read
  // .env.dev - set it in the workflow yml.
  cartStorageFallbackEnabled:
    process.env['REACT_APP_CART_STORAGE_FALLBACK_ENABLED'] === 'true',
  // --- Cart quota toast v2 (30/9, KURV-FOR-STOR-DESIGN-2026-09-30 §3 B) ---
  // The toast a quota episode shows under cart storage v2. Two customers
  // with 1-4 lines in the cart saw "Your cart is too large ..." - the cart
  // itself was not the filler, and the text hid that the cart still works
  // in the tab. On: toast.warning with the i18n key
  // orderCreate.cartStorageQuotaExceededV2 ("could not be saved ... still
  // works in this tab. Place the order now ..."). Off = byte-identical:
  // toast.error with the old key and the same options. Requires
  // cartStorageV2Enabled (the toast only exists on that path). No storage,
  // no HTTP. CI builds don't read .env.dev - set it in the workflow yml.
  cartQuotaToastV2Enabled:
    process.env['REACT_APP_CART_QUOTA_TOAST_V2_ENABLED'] === 'true',
  // --- Cart quota diagnostics (30/9, KURV-FOR-STOR-DESIGN-2026-09-30 §3 B) ---
  // When a cart write hits the browser's storage quota under cart storage
  // v2: measure the WHOLE origin (every key's length, own vs. foreign, the
  // five largest with identities sanitised), classify the episode
  // (cart-quota / cart-quota-empty / cart-quota-foreign / cart-quota-parked)
  // and send that class as the error code of the quota toast's existing
  // Clarity error-shown event; the console line carries the same plus the
  // user agent. Off = byte-identical: the original console line (payload +
  // own parked buckets only, no read of any other key) and the error code
  // "ui". Read-only, no storage writes, no HTTP. Requires
  // cartStorageV2Enabled. CI builds don't read .env.dev - set it in the
  // workflow yml.
  cartQuotaDiagEnabled:
    process.env['REACT_APP_CART_QUOTA_DIAG_ENABLED'] === 'true',
  // --- Org-id guard for three more callers (24/9, follow-up of #847) ---
  // Three FE calls could still build /organizations/NaN|null/... URLs -
  // (1) /order-details with a missing or garbled orgId/orderId in the URL
  // (useOrder(+orgId!, +orderId!) = NaN), (2) the order-details view-model's
  // commit path (reorder()/handleConfirm) for an Admin without a vendor
  // (profile.organizationId null) - NOTE: that path is exported but not
  // wired to any button in OrderDetailsComponent today, so (2) is defensive,
  // not a prod fact like (1) and (3) - and (3) the text-logo preview in the
  // design editor for a design without a customer (design.customerId null).
  // Since API #1121 those answer 404 instead of 500, but the user still
  // sees a generic error. With the flag on each caller checks for a
  // positive integer id BEFORE calling: (1) shows an "Invalid order link"
  // view with a button back to the order list and never fires useOrder,
  // (2) resolves the org the way the order-confirm screen does (admins: the
  // order's org; act-as; profile org) and warns "Select a vendor before
  // committing the order" when none, (3) falls back to the design's
  // customer/vendor/main org or the profile org and warns "The design has
  // no customer" when none. Off = byte-identical calls and DOM. CI builds
  // don't read .env.dev - set it in the workflow yml.
  orgIdGuardEnabled: process.env['REACT_APP_ORG_ID_GUARD_ENABLED'] === 'true',
  // --- Perf – listebilleder + web-vitals (PR C, DESIGN-CLARITY §7.2, 26/9) ---
  // Feature flag: list-image hygiene on /logo and /design (the colleague's
  // Clarity read 26/9: LCP 5.8 s, CLS 0.1, 29 % good views). With "true"
  // LogoImage/DesignImage rows get a reserved 110x74 box (width/height +
  // object-fit contain = no layout jump while the thumbnail loads),
  // loading="lazy" + decoding="async" from the 5th row down, fetchpriority
  // "high" on the first row (the LCP candidate), and <html data-list-perf>
  // switches the Inter font stack to one with a metric-matched Arial
  // fallback (no CLS on font swap). Attributes, classes and one CSS hook
  // only: 0 new calls, same image URLs, same rows. Honest limit: on a tall
  // viewport the browser's lazy margin still fetches all 12 rows, so the win
  // is stability and priority, not bytes. Default (unset/"false") =
  // byte-identical DOM. CI builds don't read .env.dev - set it in the
  // workflow yml.
  listPerfEnabled: process.env['REACT_APP_LIST_PERF_ENABLED'] === 'true',
  // Feature flag: field measurement of LCP / INP / CLS (web-vitals@4
  // attribution build, loaded lazily AFTER the app has rendered) sent as the
  // GA4 event "web_vitals" (metric_name, value, rating, page_path, element)
  // through the existing trackEvent facade, plus the Clarity tag
  // cwv_daarlig=<metric> on a "poor" rating. Respects the consent gate and
  // the no-analytics routes: nothing is sent before the tags are loaded
  // (= after Accept with the consent flag on), so the numbers cover
  // consenting sessions only. SPA limit (web-vitals@4 defaults): LCP is
  // reported for the hard navigation only; CLS / INP once per page
  // lifetime (at pagehide), not per route; page_path = the route the user
  // left from, landing_path = the first route (the one the LCP belongs
  // to). Read LCP per landing_path and filter on navigation_type in GA4.
  // Default (unset/"false") = today's no-op reportWebVitals() and the
  // library is never downloaded.
  webVitalsEnabled: process.env['REACT_APP_WEB_VITALS_ENABLED'] === 'true',
  // --- Admin perf (26/9, DESIGN-ADMIN-PERF): clicking around the admin/main
  // menu felt slow. Master flag + one sub-flag per package; a package is
  // active ONLY when the master AND its sub-flag are "true". Every package
  // with its flag off = byte-identical tree, options and calls. CI builds
  // don't read .env.dev - set them in the workflow yml.
  adminPerfEnabled: process.env['REACT_APP_ADMIN_PERF_ENABLED'] === 'true',
  // FE-1: a Suspense boundary around the routed <Outlet /> inside
  // AuthenticatedView (header/nav/cart stay mounted while a lazy screen
  // chunk loads - today the ONLY boundary is RootLayout's, so the first
  // click on a lazy route replaces the whole app with the splash) + chunk
  // preload from the admin dropdown (on open + per item hover/focus) and the
  // main nav hover. 0 API calls.
  adminPerfRouteSuspenseEnabled:
    process.env['REACT_APP_ADMIN_PERF_ENABLED'] === 'true' &&
    process.env['REACT_APP_ADMIN_PERF_ROUTE_SUSPENSE_ENABLED'] === 'true',
  // FE-2: no re-render of the whole provider chain per route change
  // (useNavigate moved out of PreLoader/ProfileProvider into null leaf
  // components; memoised Provider values; memoised ApiClient/authFetch).
  adminPerfNavMemoEnabled:
    process.env['REACT_APP_ADMIN_PERF_ENABLED'] === 'true' &&
    process.env['REACT_APP_ADMIN_PERF_NAV_MEMO_ENABLED'] === 'true',
  // FE-3: admin screens with staleTime 0 paint at once from a 30 min cache
  // and refetch ONCE in the background on entry (refetchOnMount +
  // cacheTime + keepPreviousData on exactly those keys).
  adminPerfAdminSwrEnabled:
    process.env['REACT_APP_ADMIN_PERF_ENABLED'] === 'true' &&
    process.env['REACT_APP_ADMIN_PERF_ADMIN_SWR_ENABLED'] === 'true',
  // FE-4: helper lists (all customers/vendors, logo options, folders)
  // survive 30 min in cache (TTL <= 60 s for the user-mutable ones, with
  // invalidation after own mutations) + soft gates: the design/style lists
  // paint when their primary query lands instead of waiting for
  // GetManufacturers (p50 4.2 s) / GetStyles.
  adminPerfHelperCacheEnabled:
    process.env['REACT_APP_ADMIN_PERF_ENABLED'] === 'true' &&
    process.env['REACT_APP_ADMIN_PERF_HELPER_CACHE_ENABLED'] === 'true',
  // FE-5: staleTime 60 s on GetOrders / GetAwaitingPaymentSummary (0 calls
  // on a re-visit within 60 s; own mutations still invalidate).
  adminPerfOrdersStaleEnabled:
    process.env['REACT_APP_ADMIN_PERF_ENABLED'] === 'true' &&
    process.env['REACT_APP_ADMIN_PERF_ORDERS_STALE_ENABLED'] === 'true',
  // FE-6: the header queue badges (import-logos/import-designs) poll every
  // 60 s only while the dropdown is open, else every 5 min, and refetch on
  // open when stale (never older than 60 s when looked at).
  adminPerfQueueLazyEnabled:
    process.env['REACT_APP_ADMIN_PERF_ENABLED'] === 'true' &&
    process.env['REACT_APP_ADMIN_PERF_QUEUE_LAZY_ENABLED'] === 'true',
  // --- Vendor list page size (27/9, ANALYSE-ADMIN-STALL-PRISIMPORT rang 1) ---
  // The "all vendors" helper list (getAllQuery = page size 0) is sent WITHOUT
  // pageSize (toQueryParam drops 0), so the API answers its default page of
  // 25 rows (name-descending) - the customer-create vendor picker and the
  // design/logo/order lists' admin vendor filter silently see 25 of ~129
  // vendors, while the act-as and logo-sharing pickers ask for
  // pageSize=10000 (every vendor with its full Children/Parent/Commission
  // graph = the 120-180 s responses). The sheet-size page has its own
  // explicit pageSize=0 list since #875, which since API 47e555cb means
  // "no paging" in BOTH API states; ON adds all=true to that call too.
  // ON: every "all vendors" query gets all=true (vendorListQuery.ts), which
  // the API's admin branch (VendorListAdminPagedEnabled) answers with the
  // whole lean list DTO, and each KEEPS its page size so an API without
  // that branch (it ignores `all`) behaves exactly as today: lookup
  // "?pageIndex=1&all=true" (25 there, as today), pickers
  // "?pageSize=10000&pageIndex=1&all=true" (every vendor there, as today).
  // Two cache keys as today, 30 min helper cache. The admin vendor list
  // keeps its real page (pageSize=24/48/96). OFF: byte-identical query
  // strings and calls.
  vendorListPageSizeEnabled:
    process.env['REACT_APP_VENDOR_LIST_PAGE_SIZE_ENABLED'] === 'true',
  // --- Role route redirect (27/9): "Ikke fundet" after login / act-as ---
  // A role-gated route (AuthRoute) rendered in place as NotFoundPage when
  // the loaded profile's role is too low - e.g. a vendor/customer identity
  // landing on /manufacturer via the role-blind post-login deep link
  // (sessionStorage "redirectOnLogin", not cleared by logout) or a reload
  // under act-as. With the flag on: (1) AuthRoute navigates (replace) to the
  // role's home /logo instead, (2) logout() clears "redirectOnLogin" so an
  // admin tab's marker never inherits into the next identity. NotFound for
  // a missing profile is untouched. Off = byte-identical. CI builds don't
  // read .env.dev - set it in the workflow yml.
  roleRouteRedirectEnabled:
    process.env['REACT_APP_ROLE_ROUTE_REDIRECT_ENABLED'] === 'true',
  // --- Design prefetch lean (28/9, PLAN-A9-PRISTOP-ARTBOARDS §A.3 / §E.3) ---
  // Hypothesis from prod 28/9 (not proven - the API's [DesignListSize] line
  // decides): the isolated worker's memory peaks (+3.5-4.5 GB) line up with
  // bursts of GetDesigns for large orgs (chain orgs 1,278-2,684 designs, org
  // 1374 18,999), and every visible design page also fires hidden prefetches
  // (next page after 500 ms, paginator hover, the logo list's cross-prefetch
  // of the design first page). With the flag on: (1) the design list skips
  // the next-page and hover prefetch when the current result's totalCount
  // is over designPrefetchMaxTotal (an unknown total = today); (2) the logo
  // list skips the cross-prefetch of the design first page ONLY when the
  // cache already holds that page with a total over the limit (unknown =
  // today, 0 new calls to find out); (3) every prefetchDesigns call carries
  // x-km-op: prefetch so the API's list telemetry can tell prefetch from
  // clicks. A click still fetches exactly as today (same key, same call).
  // Default (unset/"false") = byte-identical DOM and wire (no header, same
  // prefetches). CI builds don't read .env.dev - set it in the workflow yml.
  designPrefetchLeanEnabled:
    process.env['REACT_APP_DESIGN_PREFETCH_LEAN_ENABLED'] === 'true',
  // The totalCount limit of the lean prefetch above (read only when that
  // flag is on). Unset, unparsable or 0 = 1000 (Nichlas 28/9 §F.6: small
  // orgs keep their prefetch).
  designPrefetchMaxTotal:
    parseInt(process.env['REACT_APP_DESIGN_PREFETCH_MAX_TOTAL'] ?? '1000', 10) || 1000,
  // --- NAV unavailable = fail fast (29/9, NAV-ROBUSTHED-2026-09-29.md §7.2) ---
  // Prod 29/9 06:20-07:0x UTC: NAV could not reach its own SQL Server and
  // every NAV call waited 21 s for Windows' TCP-connect timeout, so "Place
  // order" and the delivery-date preview answered 400 with raw NAV text
  // after 21 s and the item lookups 500. PAIRED with the API's
  // NavFailFastEnabled: while its NAV health latch is open the API answers
  // the customer routes 503 { code: "navUnavailable", reason, retrySafe,
  // retryAfterSeconds } in < 1 s (0 NAV calls). On: errorStatusFromBody
  // keeps the marker + retryAfterSeconds + reason + retrySafe, withToasts
  // shows ONE translated toast (nav_unavailable in all 10 locales) instead
  // of the compound "Could not place order: <raw NAV text>", place/commit
  // retry ONCE after retryAfterSeconds (clamped 2-15 s) ONLY when the API
  // said retrySafe (refused before anything was written), the confirmation
  // page shows an amber "delivery date could not be checked" line (Place
  // order stays enabled), and the three NAV lookups (delivery-date-preview,
  // verify-storage, navision/items) get the client timeout below. 0 new
  // calls. Default (unset/"false") = byte-identical DOM and wire. CI builds
  // don't read .env.dev - set it in the workflow yml. Prod: only together
  // with the API's PR-B (NavisionService passage) - see the plan §5.
  navUnavailableEnabled:
    process.env['REACT_APP_NAV_UNAVAILABLE_ENABLED'] === 'true',
  // Client-side timeout (ms) for the three NAV lookups above via
  // AbortController - the safety net when the API itself does not answer
  // (its own deadline is shorter when NavCustomerTimeoutSeconds is set).
  // Only read when navUnavailableEnabled is on; an abort surfaces as the
  // same error family (the packaging price is simply left out, the
  // delivery date keeps its fallback, the storage verify stays silent -
  // exactly what the callers do for any failure today). Unset, empty,
  // non-numeric or <= 0 = no timeout (byte-identical wire); values under
  // 5000 are lifted to 5000 (same parser as the upload timeout). Dev 20000.
  navLookupClientTimeoutMs: parseUploadClientTimeoutMs(
    process.env['REACT_APP_NAV_LOOKUP_CLIENT_TIMEOUT_MS']
  ),
  // --- Max logo size per transfer method, managed from admin (30/9,
  // LOGO-MAKS-300x400-DESIGN-2026-09-30.md) ---
  // Nichlas 30/9: a logo may be at most 300x400 mm (= 400x300; 350x350 is not
  // allowed) for Standard/Industri/Colorstop2 etc., and the values must be
  // editable by an admin without a deploy. PAIRED with the API's
  // LogoMethodMaxSizeEnabled: the API owns the limit (ONE policy for upload,
  // resize, method change, clone and approval - a logo above it is SCALED
  // DOWN, never refused) and overrides `size` in the method list and in each
  // logo's transferMethod - the FE has NO limits of its own. On:
  // (1) Admin -> Methods gets the box "Max logo size" (GET/PUT
  //     ops/transfer-methods/max-sizes; a 404 = API flag off = no box). Every
  //     save sends the revision the page holds; a 409 (somebody else saved
  //     in between) shows the API's message, loads the stored state from the
  //     answer and keeps the admin's edits on top - nothing is overwritten
  //     unseen. The latest changes are listed under the table.
  // (2) the editor's method change fits the size exactly as the API does
  //     (fitSizeToTransferMethod: whole mm, 400x300 stays 400x300 under
  //     "300x400"); in the design editor "Change size" after an unsaved
  //     method change saves the method first; the standalone editor's
  //     automatic resize after a method change sends whole mm.
  // (3) the API's answer says when it scaled a logo down (maxSizeClamp):
  //     a clamped clone gets no size restore (saves one resize call), and
  //     approval / resize / clone show ONE toast that also says the size
  //     changed.
  // Default (unset/"false") = byte-identical DOM and wire (the box is not
  // mounted = 0 calls). CI builds don't read .env.dev - set it in the
  // workflow yml.
  logoMethodMaxSizeEnabled:
    process.env['REACT_APP_LOGO_METHOD_MAX_SIZE_ENABLED'] === 'true',
  // --- Customer-specific max logo size (30/9,
  // LOGO-MAKS-PR-KUNDE-DESIGN-2026-09-30.md) ---
  // Nichlas 30/9: the max logo size must also be settable for selected
  // customers. PAIRED with the API's LogoMethodMaxSizeByOrgEnabled: the API
  // owns the values and resolves them in the SAME policy (the logo's own
  // customer -> an organisation above it that passes its values on -> the
  // method's general max size) - the FE still has NO limits of its own.
  // Needs the flag above as well (the API's flag needs its general one the
  // same way). On:
  // (1) Admin -> Methods gets the box "Customer-specific max sizes" under
  //     "Max logo size" (GET/PUT ops/transfer-methods/max-sizes/customers;
  //     a 404 = API flag off = no box, never a toast): add a customer with
  //     the organisation search, one field per method, remove a customer,
  //     saved with the revision the page holds (409 as the general box),
  //     latest changes underneath.
  // (2) the editor's method change takes the limit the logo's OWN answer
  //     carries for its customer (LogoDto.methodMaxSizes, detail and
  //     mutation answers only) before the method list's general size. The
  //     method list (logos/options/all, cache key ["logo-options"]) never
  //     carries a customer's value and is never written to: the helper
  //     returns a copy, so nothing can follow into another logo, customer
  //     or act-as identity.
  // Default (unset/"false") = byte-identical DOM and wire (the box is not
  // mounted = 0 calls; the helper returns the method it was given). CI
  // builds don't read .env.dev - set it in the workflow yml.
  logoMethodMaxSizeByOrgEnabled:
    process.env['REACT_APP_LOGO_METHOD_MAX_SIZE_ENABLED'] === 'true' &&
    process.env['REACT_APP_LOGO_METHOD_MAX_SIZE_BY_ORG_ENABLED'] === 'true',
  // Flag REACT_APP_REMEMBER_PAGE_SIZE_ENABLED (Bait via Tom 1/10): the page
  // size a user picks in a list footer (logos, designs, orders, customers,
  // vendors, styles, manufacturers) is remembered per user and per list in
  // localStorage (`${b2CId}_pageSize_${listKey}`, src/utils/pageSizeMemory)
  // and used as that list's default on the next visit when the URL carries
  // no pageSize; a URL with pageSize (shared link, Back/Forward) wins as
  // today and is remembered too. Only a size the list's footer offers is
  // ever read or written; no profile = no memory. The boot and cross
  // prefetches warm the remembered size so their cache keys still match
  // (0 new calls). Default (unset/"false") = byte-identical behaviour: URL
  // query -> default 12 (orders 25), localStorage never read or written.
  // CI builds don't read .env.dev - set it in the workflow yml.
  rememberPageSizeEnabled:
    process.env['REACT_APP_REMEMBER_PAGE_SIZE_ENABLED'] === 'true',
  // Flag REACT_APP_LOGO_OPTIONS_COMPACT_COLORS_ENABLED (LOGOLISTE-LAST 3/10
  // §3 E2). PAIRED with the API's LogoOptionsCompactColorsEnabled: with that
  // on, GET logos/options/all leaves `methods` out of every colour in the
  // three colour lists (null) and says colorMethodsCompact: true - the
  // per-colour method keys are the bulk of the 2,8 MB answer. On: when the
  // answer carries that hint, mapLogoOptions derives each colour's methods
  // with the API's own rule (ListLoadPolicy.ColorMethods = LogoService.
  // GetAvailableColors): every method of transferMethodsAll, minus those
  // without neon support for a neon colour and those without metallic
  // support for a metallic colour (utils/functions/logoOptionsCompactColors).
  // An answer without the hint is used as is, so the flag is safe to turn
  // on before the API's; it MUST be on before the API's flag is (a compact
  // answer read with this off has methods: null). 0 new calls; the cart
  // price check never reads colour methods. Default (unset/"false") = the
  // answer is used as is = byte-identical. CI builds don't read .env.dev -
  // set it in the workflow yml.
  logoOptionsCompactColorsEnabled:
    process.env['REACT_APP_LOGO_OPTIONS_COMPACT_COLORS_ENABLED'] === 'true',
  // Flag REACT_APP_STOCK_STATUS_REPORT_ADMIN_ENABLED (Nichlas 3/10): the
  // admin page "Stock status report" (menu item in the E-mail group + route
  // admin/stock-status-report): the list of stock logos (name, colour, size,
  // item no., per batch, enabled) and the recipients of the monthly stock
  // status mail the API sends at 23:59 Danish time on the last day of the
  // month, with Preview (the figures right now, the change since the
  // previous measurement, a status per item) and "Send test now". PAIRED
  // with the API app setting StockStatusReportEnabled (a 404 is said as
  // "not active"). Calls: ONE GET ops/stock-status-report per visit, ONE GET
  // .../preview per Preview click, ONE PUT per Save, ONE POST .../send-now
  // per confirmed test send; nothing polls. Default (unset/"false") = no
  // route, no menu item, 0 calls = byte-identical. CI builds don't read
  // .env.dev - set it in the workflow yml.
  stockStatusReportAdminEnabled:
    process.env['REACT_APP_STOCK_STATUS_REPORT_ADMIN_ENABLED'] === 'true',
  // Feature flag: public page /public/migration1 (Nichlas 4/10) - the
  // old-webshop migration flow (same three steps and the same two calls as
  // /public/migration: ONE validate-old-credentials per "move my account"
  // click, ONE migrate-from-old-webshop per "create account" click) in the
  // KitMaker front page design (the ID UNLIMITED login page: photo hero,
  // white panel, Pulse Green). /public/migration (the old-webshop lookalike)
  // and the migration hosts' front page are untouched. The route is
  // registered in both route tables only when this is on; 0 new API calls,
  // nothing on mount. Default (unset/"false") = no route = byte-identical
  // (the path gives the same result as any unknown path). CI builds don't
  // read .env.dev - set it in the workflow yml.
  publicMigration1Enabled:
    process.env['REACT_APP_PUBLIC_MIGRATION1_ENABLED'] === 'true',
  // Feature flag: the selected product in the cart strip (a reseller 7/10:
  // "with many MUL items to add sizes and names to, it is hard to see how
  // far I have got in the overview"). On = the strip's card for the line
  // shown below it gets a strong frame in the theme colour (the same
  // --primary as Continue / Add row, with a darker edge so the frame keeps
  // >= 3:1 against white), the faint theme background and a small "Selected
  // product" check badge (top left - the other three corners are taken by
  // the remove cross, the price mark and the oversize mark); aria-current
  // on that card; every card becomes keyboard-focusable (role button,
  // Enter/Space selects) with a visible focus outline; and the strip
  // scrolls the selected card into view horizontally when it sits outside
  // the strip (never a page scroll on its own). The mark follows a click
  // on another card, a new line (which is selected, as today) and the
  // removal of the selected line. 0 new API calls, no data change - the
  // selected id already lives in useOrderCreateViewModel and is only
  // handed to the strip. Default (unset/"false") = no classes, no
  // attributes, no scroll = byte-identical. CI builds don't read .env.dev -
  // set it in the workflow yml.
  cartSelectedLineHighlightEnabled:
    process.env['REACT_APP_CART_SELECTED_LINE_HIGHLIGHT_ENABLED'] === 'true',
  // Feature flag: cart rows start with an EMPTY size (Nichlas 7/10, order
  // screen with a bag MUL203550 showing size "M": "not M - blank as a
  // start, maybe an example on mouseover"). On: every NEW row (the first
  // row when a design is added to the cart, "add row" in the cart panel and
  // in the public names/numbers page, a bare name list from the Excel
  // upload) gets size "" instead of "M", and the size field shows an
  // example (title on hover, placeholder on keyboard focus, sr-only text
  // via aria-describedby). Rows already in the cart keep their size. An
  // empty size is allowed today (no validation, sent as "" = NAV
  // designSize=""). 0 new API calls. Default (unset/"false") = "M" and the
  // plain field = byte-identical. CI builds don't read .env.dev - set it in
  // the workflow yml.
  cartSizeBlankDefaultEnabled:
    process.env['REACT_APP_CART_SIZE_BLANK_DEFAULT_ENABLED'] === 'true',
  // Feature flag: Continue in the cart confirms rows without a size
  // (Nichlas 7/10, follow-up to the blank default size: a shirt row must
  // not slip through without a size, but KitMaker does not know a
  // product's valid sizes - the size is free text - so a bag or a cap must
  // still pass). On: when Continue is pressed and at least one produced
  // design row (toDelivery + toStorage > 0, not a logo line, not a line the
  // customer fills in themselves) has an empty/whitespace size, a dialog
  // lists the products ("<name> (<no>): n row(s) without size") with "Go
  // back and fill in sizes" (primary; closes, marks the empty size fields
  // red + aria-invalid until filled) and "Continue without sizes" (exactly
  // today's Continue). Esc/backdrop = go back. Never a block, 0 new API
  // calls, nothing stored. Default (unset/"false") = Continue straight
  // through, no dialog in the DOM = byte-identical. CI builds don't read
  // .env.dev - set it in the workflow yml.
  cartSizeMissingConfirmEnabled:
    process.env['REACT_APP_CART_SIZE_MISSING_CONFIRM_ENABLED'] === 'true',
  // Feature flag: a wider size field in the cart rows (Nichlas 7/10: «feltet
  // skal have en størrelse hvor der må stå lidt mere end M men også 164,
  // 152 osv.»). On: the size column grows 90 -> 136 px (inner 106 px: "e.g.
  // S, M, L, XL" = 84 px and "152/164" = 44 px at text-xs Inter stand fully)
  // and the row-label column shrinks 330 -> 284 px by the same 46 px ("Design
  // 12" = 55 px), so the row keeps its width to the pixel: the qty column,
  // the expand chevron and the trash icon do not move and no breakpoint
  // (desktop, tablet, the public page at 375 px) gets a wider row. The
  // column heading SIZES follows, maxLength goes 10 -> 20 and the keyboard
  // focus placeholder is the short example (orderCreate.sizeExampleShort,
  // <= 97 px in every locale) while title / sr-only keep the full one. 0
  // new API calls. Default (unset/"false") = today's class strings byte for
  // byte. CI builds don't read .env.dev - set it in the workflow yml.
  cartSizeFieldWideEnabled:
    process.env['REACT_APP_CART_SIZE_FIELD_WIDE_ENABLED'] === 'true',
  // Feature flag: free-text "Color" filter in the logo list's filter row
  // (colleague 14/10: type a colour code such as 877c and get every logo
  // that carries that colour). On: a text field next to the dropdown
  // filters (same height and look); the value rides on the ONE existing
  // list request as filterByField=color&filterByValue=<text>, sent only
  // after Enter or 600 ms without a keystroke and only with at least 2
  // characters; it counts in "Active filters (n)", is cleared by Clear /
  // Reset filters and remembered by Remember filters like the others. 0 new
  // API calls. PAIRED with the API flag LogoListColorFilterEnabled - API
  // first (what the API does with a color field while its flag is off is
  // today's behaviour, documented in the API PR). Default (unset/"false") =
  // no field, no color key anywhere = byte-identical. CI builds don't read
  // .env.dev - set it in the workflow yml.
  logoColorFilterEnabled:
    process.env['REACT_APP_LOGO_COLOR_FILTER_ENABLED'] === 'true',
  // Feature flag: hide the "Preview prices with heating fees" switch in the
  // design popup (DesignPrice) when EVERY logo on the design is embroidery
  // (Nichlas 14/10, a customer's MUL352296 with two Embroidery logos: «det er
  // forvirrende at der på broderi-MUL står at de kan se priser med
  // påvarmningsgebyr»). Heating only ever applies to transfer logos, so on
  // an embroidery-only design the switch changes nothing (transferLogoCount
  // = 0) and only confuses. On: the row is left out when the design has at
  // least one logo and all of them have the NAV method type Broderi /
  // Embroidery (the same type the popup's logo card shows); mixed types, no
  // logos or a logo without a type = the row as today. The "Preview prices
  // with logo fees" switch is never touched (embroidery can carry logo
  // fees). Admin, dealer and customer share the component. 0 new API calls.
  // Default (unset/"false") = byte-identical. CI builds don't read .env.dev
  // - set it in the workflow yml.
  heatingToggleHideEmbroideryOnlyEnabled:
    process.env['REACT_APP_HEATING_TOGGLE_HIDE_EMBROIDERY_ONLY_ENABLED'] ===
    'true',
  // Feature flag: "Reject and fix it myself" in the dealer's reject popup
  // (Nichlas 14/10, variant A: the SAME design and the SAME MUL all the way).
  // On: a third radio next to "Delete this product" / "Own remark" for a
  // pending design that has a style (the field the editor requires); the
  // remark is OPTIONAL here (Nichlas 17/10: no note when the dealer fixes it
  // themselves; "Own remark" still requires it) and NO e-mail goes to the
  // customer (KitMaker sends none on a verdict; the API stores a fixed
  // internal note for a blank one). Confirm = today's ONE PUT
  // .../designs/{id}/approval with { approve:false, rejectionNote,
  // editAfterReject:true } (NAV gets AFVIST + the remark as today; the API
  // unlocks the design for the editor), the response is landed in the
  // caches in place (0 calls, no GetDesigns refetch) and the popup navigates
  // straight to the editor of that design (same route as Edit). When the
  // API answers editAfterRejectApplied:false (a locked design without a
  // style) the design is rejected as today and a toast says to use Clone.
  // The popup's Edit button is also shown for a REJECTED design that has a
  // MUL (today it is hidden for every design with a MUL), still gated on
  // isEditable - the row's Edit already shows it, so the two agree. The
  // customer's reject dialog is untouched. 0 new calls. PAIRED with the API
  // flag DesignRejectAndEditEnabled (API first; with the API flag off the
  // extra body property is ignored and the editor may open on a locked
  // design - keep this flag off until the API flag is on). Default
  // (unset/"false") = no radio, today's PUT body, today's Edit gate =
  // byte-identical. CI builds don't read .env.dev - set it in the workflow
  // yml.
  rejectAndEditEnabled:
    process.env['REACT_APP_REJECT_AND_EDIT_ENABLED'] === 'true',
  // Feature flag: "Reject and fix it myself" for LOGOS (Nichlas 17/10: «vil
  // du gøre det samme så vi kan styre det på logoer også … hvis det blot er
  // en metode, farve eller størrelse»). On: the dealer's reject dialog of a
  // logo awaiting their verdict (the popup) gets a third radio "Reject and
  // fix it myself" - no note required, no e-mail is sent - which sends
  // today's ONE verdict PUT with editAfterReject:true, lands the answer in
  // the caches exactly as every verdict does (0 extra calls) and opens the
  // logo editor on the SAME logo (same route as Edit), where method, colours
  // and size are changed. The customer never sees it, orders never, and a
  // logo already committed (a real serial as SKU - where Edit is hidden
  // today) never. PAIRED with the API flag LogoRejectAndEditEnabled (API
  // first: with the API flag off the body property is ignored and a blank
  // note is stored blank - the answer then carries no editAfterRejectApplied
  // and the FE shows the error toast instead of opening the editor, so the
  // wrong order is visible; still keep this flag off until the API flag is
  // on). Default (unset/"false") = no radio, today's PUT body, today's DOM =
  // byte-identical. CI builds don't read .env.dev - set it in the workflow
  // yml.
  logoRejectAndEditEnabled:
    process.env['REACT_APP_LOGO_REJECT_AND_EDIT_ENABLED'] === 'true',
  // Feature flag: "Fix it myself" on a logo ALREADY COMMITTED to the supplier
  // (a real item number as SKU; Nichlas 17/10: «det skal altid være det samme
  // [varenummer] hvis vi retter» / «den skal bare gøre det samme som når
  // JS-upload pusher igen»). On, and only together with
  // logoRejectAndEditEnabled (the offer and the Edit gates check BOTH flags
  // - canRejectAndEditCommittedNavLogo; the pending badge and the B3 order
  // lock need only this flag, since they show the API's state): the dealer's
  // reject dialog of a committed NAV logo awaiting their verdict also offers
  // "Fix it myself" when the API says
  // navWriteBackEligible (a NAV item with a real item number, a transfer /
  // direct-print method, not shared with every customer, the caller a
  // vendor) and no write-back is pending; the dialog adds the hint that the
  // item number stays the same and the changes go to the supplier system.
  // The popup's Edit is shown for such a logo in the Rejected layout when the
  // rejection is the dealer's own (approver not the import's "SYSTEM"). After
  // "Save changes & approve" the API answers navWriteBackPending=true while
  // its worker writes method / size / colours to NAV under the SAME item
  // number and re-imports: the list row and the popup show "Sending to
  // supplier" (the design's NavPendingBadge look), and "Add to order" (row,
  // popup and bulk "Add to Order (n)", which skips the logo with a toast)
  // and Edit are hidden meanwhile (the price is the old one until the
  // re-import lands). The editor's colour tool stays visible unless the API's
  // navWriteBackFields leaves "colour" out. 0 new calls - the three fields
  // ride on every logo read. PAIRED with the API flag LogoNavWriteBackMode
  // (off|shadow|on; the fields are absent while it is off, so this flag is
  // inert = today's rule). Default (unset/"false") = today's gates and DOM,
  // byte for byte. CI builds don't read .env.dev - set it in the workflow
  // yml (dev flip = its own PR).
  logoRejectAndEditNavEnabled:
    process.env['REACT_APP_LOGO_REJECT_AND_EDIT_NAV_ENABLED'] === 'true',
  // Feature flag: no "Send Approval to Customer" for a logo the dealer is
  // FIXING THEMSELVES (18/10, ANALYSE-LOGO-1102300-FARVE-NAV §3.3 H2 / §4
  // B3(b)): the customer path (approval-start skip=false + the customer's
  // verdict) never runs the API's NAV write-back - only the dealer's own
  // skip approval does - so a fix sent to the customer first would never
  // reach the supplier system. On: in the LOGO EDITOR's approval dialog the
  // "Send Approval to Customer" button is disabled, with a help line that
  // points to the skip link, while the logo is navWriteBackEligible AND the
  // vendor rejection is the dealer's own (isVendorsOwnRejection); once the
  // dealer has approved the fix the gate opens again. "Add to order" and the
  // skip link are untouched; the logo list's dialog and the design callers
  // are untouched; 0 new calls. Its own flag: the two reject-and-edit flags
  // never turn it on. Default (unset/"false") = today's dialog, byte for
  // byte. CI builds don't read .env.dev - set it in the workflow yml (dev
  // flip = its own PR).
  logoRejectAndEditBlockCustomerSendEnabled:
    process.env['REACT_APP_LOGO_REJECT_AND_EDIT_BLOCK_CUSTOMER_SEND_ENABLED'] === 'true',
  // Feature flag: a visible warning when a "Fix it myself" NAV logo is
  // approved but the fix is NOT sent to the supplier system (18/10,
  // ANALYSE-LOGO-1102300-FARVE-NAV §3.2 / §4 B2: logo 730891 was shared
  // with another organisation, so the API's write-back policy skipped the
  // commit with one sampled log line and the dealer saw only «Approval
  // skipped»). On: the three skip=true approval starts (the dialog's
  // "Continue without ordering" / "Add to order", the design editor's "Save
  // Changes & Approve") read navWriteBackSkippedReason from the API's
  // approval-start answer and show ONE 15 s warning - one text per known
  // reason (shared-item, not-owned, not-rejected, vendor-not-rejected,
  // not-own-rejection) and a generic one that names the reason - on top of
  // the flow's own success toast. No reason in the answer = no warning.
  // PAIRED with the API flag LogoNavWriteBackSkipReasonInDtoEnabled (the
  // field is absent while it is off / on an older API, so this flag alone
  // changes nothing; either order is safe). 0 new calls. Default
  // (unset/"false") = the answer's promise handed back untouched, the field
  // never read = today's silence, byte for byte. CI builds don't read
  // .env.dev - set it in the workflow yml (dev flip = its own PR).
  logoNavWriteBackSkipReasonEnabled:
    process.env['REACT_APP_LOGO_NAV_WRITE_BACK_SKIP_REASON_ENABLED'] === 'true',
  // Feature flag: order only approved designs and logos (Zendesk #724425,
  // Nichlas 15/10: «De kan markere flere MUL og bestille på korrektur, der
  // ikke er godkendt – kan vi få stop på det»). One rule, shared with the
  // API guard OrderDesignApprovalGuardMode (utils/functions/orderability):
  // a design is orderable when status is done AND no participant
  // (vendor/customer/external) is Pending or Rejected; a logo only when its
  // status is done. On: the bulk "Add to Order (n)" / "Add to cart" skip
  // non-orderable rows with a toast («n designs skipped – approval pending»)
  // and are disabled with a hint when nothing selected is orderable - the
  // multi-select checkbox itself is NEVER disabled by the guard (Nichlas
  // 16/10: the dealer must tick AWAITING rows and send a proof to several
  // at once; the first prod day's checkbox gate is gone), the design row's
  // "Add to order" is replaced by the
  // popup's rule (no Stepper / Add to order while approval is pending), and
  // the API's 400 designNotApproved / logoNotApproved at place/commit order
  // is shown as ONE readable toast naming the MULs (the cart stays intact).
  // 0 new API calls - everything is decided on fields the lists already
  // carry. Default (unset/"false") = byte-identical UI and wire. CI builds
  // don't read .env.dev - set it in the workflow yml (own PR).
  designOrderabilityGuardEnabled:
    process.env['REACT_APP_DESIGN_ORDERABILITY_GUARD_ENABLED'] === 'true',
  // Org-id list twin of the flag above (19/10, Nichlas: «lad denne i første
  // omgang kun være tilgængelig med en ejer, altså en vendor»): the order
  // guard's UI (row / popup rule, bulk filter + skip toast, the readable 400
  // toast) for exactly these organisation ids - the viewer's own
  // organisation or its parent (a customer under a listed dealer) - while
  // the flag stays false. Pairs with the API's
  // OrderDesignApprovalGuardEnforceOrgIds (enforce for the listed
  // organisations, today's mode for everybody else). Separators , ; and
  // whitespace; positive integers only, de-duplicated, noise dropped.
  // Build-time, and CI builds don't read .env.prod: the release branch sets
  // the id in the Build env block of
  // .github/workflows/main_app-kitmaker-prod.yml (what the prod bundle
  // reads) AND mirrors it in .env.prod (local builds; the pin test
  // enviroment.designOrderabilityGuardOrgIds.test.ts requires the two to
  // agree) - a new bundle; dev runs on the flag itself. Unset / empty = []
  // = byte-identical.
  designOrderabilityGuardOrgIds: (process.env['REACT_APP_DESIGN_ORDERABILITY_GUARD_ORG_IDS'] ?? '')
    .split(/[,;\s]+/)
    .filter((id) => /^\d+$/.test(id))
    .map(Number)
    .filter((id, index, ids) => id > 0 && ids.indexOf(id) === index),
  // Feature flag: the dealer may order a design the customer still has to
  // approve (Nichlas 18/10, MUL352937: «som vendor selvom den er sendt ud
  // til kunden skal man kunne bestille stadig væk»). On: the dealer's own
  // row on a design under approval with the dealer's vote Approved and the
  // customer's Pending (external null / Approved) shows Stepper + "Add to
  // order" + Delete again; the click runs the popup's two calls, awaited:
  // POST approval-start {skip:true} (the API settles the group under ITS
  // flag DesignVendorSettlePendingCustomerEnabled and commits to NAV), then
  // "Add to order" as today - only when the answer is orderable by the one
  // rule (isDesignOrderable), else the skip toast and nothing in the cart.
  // The customer's and the admin's rows, the popup and the bulk "Add to
  // Order (n)" are untouched. Default (unset/"false") = byte-identical UI
  // and wire. CI builds don't read .env.dev - set it in the workflow yml
  // (own PR, after the API flag is on in dev).
  vendorOrderWhileCustomerPendingEnabled:
    process.env['REACT_APP_VENDOR_ORDER_WHILE_CUSTOMER_PENDING_ENABLED'] === 'true',
  // Feature flag: a logo the dealer has approved is orderable although the
  // customer never answered (19/10, prod 05:56-06:51Z: the API guard
  // OrderDesignApprovalGuardMode=enforce refused 12 PlaceOrder attempts at
  // 4 customer orgs for logos the list shows as «APPROVED BY · Vendor» but
  // which are stuck in LogoEditStatus.Validating - sent to the customer with
  // approval-start skip=false, and Done is set only by the customer's
  // answer; before 16/10 they were orderable). On: the one logo rule
  // (utils/functions/orderability isLogoOrderable) also accepts a
  // Validating image logo whose vendor vote is Approved and where no
  // participant (vendor / customer / external) is Rejected - for the dealer
  // and the customer alike - so the list row shows Stepper + "Add to order"
  // instead of "View designs", and the bulk "Add to Order (n)" no longer
  // skips it and is enabled for it. Validating without the dealer's
  // approval, Draft and Rejected stay blocked; the B3 supplier write-back
  // rule (logoRejectAndEditNavEnabled) is untouched. 0 new calls - decided
  // on the approval group every list row already carries. PAIRED with the
  // API flag OrderApprovalGuardLogoVendorApprovedOrderableEnabled (the same
  // form in OrderApprovalGuardPolicy.LogoBlockReason; API first, else the
  // guard still answers 400 to the order the row now offers). Default
  // (unset/"false") = today's rule §1 (status done only) and today's DOM,
  // byte for byte. CI builds don't read .env.dev - set it in the workflow
  // yml (dev flip = own PR).
  logoVendorApprovedOrderableEnabled:
    process.env['REACT_APP_LOGO_VENDOR_APPROVED_ORDERABLE_ENABLED'] === 'true',
  // Feature flag: "Sales price markup" for dealers (FORSLAG-AVANCE-PROCENT
  // 15/10, variant A). On: a dealer (Vendor, or an admin impersonating a
  // vendor org) can switch on a markup % in My profile > Commission & VAT
  // (and quick-toggle it in the header menu); every price the dealer sees
  // on screen (logo list, logo/design popups, cart, checkout, order details,
  // fees, heating, delivery fees) is then shown as the SALES price =
  // purchase price x (1 + pct/100) with a "Sales price" badge, and the
  // purchase price is not shown. A customer-specific commission (the
  // adjustedCustomerPrice the API already sends) wins over the general %.
  // Pure display: nothing sent to the API, the order, NAV, the mails, Stripe
  // amounts, the admin price adjustment and the commission editors are never
  // multiplied. The setting itself rides on the existing profile call
  // (displayMarkupEnabled/-Percent, present only while the API app setting
  // VendorDisplayMarkupEnabled is on) and is saved with ONE PUT
  // organizations/{orgId}/display-markup. 0 new calls otherwise. PAIRED
  // with the API flag (API first; without it the profile carries no fields
  // = the control is not shown). Default (unset/"false") = no control, no
  // badge, every price exactly as today = byte-identical. CI builds don't
  // read .env.dev - set it in the workflow yml.
  priceMarkupEnabled:
    process.env['REACT_APP_PRICE_MARKUP_ENABLED'] === 'true',
  // Feature flag: translated logo-list texts (Nichlas 17/10: «oversættelse
  // af disse også, som udelukkende er på engelsk nu»). On: the Status, Type
  // and Method filter dropdowns (logo list AND design list), the method line
  // under a logo's title (logo list row, customer edit screen's Logos
  // section) and the Type/Method lines in the logo popup, the design popup
  // and the design editor's logo card show translated DISPLAY texts, and
  // every screen-print method variant (NAV type DIREK TRYK, or a method
  // named "Screen Print ...") is shown as ONE value - EN "Screen Print",
  // DA "Direkte tryk" - in the Method filter, the rows and the popups.
  // Picking that merged value filters on typeFilter=DIREK TRYK (a field the
  // API already supports), never on a synthetic method key. Pure display:
  // filter VALUES sent to the API, enum/NAV names, getTypeText and every
  // comparison on them (PDFs, CO2, heating switch) are untouched, and 0 new
  // calls. Default (unset/"false") = today's texts and option lists,
  // byte-identical. CI builds don't read .env.dev - set it in the workflow
  // yml (own PR).
  logoListI18nEnabled:
    process.env['REACT_APP_LOGO_LIST_I18N_ENABLED'] === 'true',
  // Feature flag: the approval e-mail sections of My profile in the page's
  // own visual language (Nichlas 17/10: «fiks denne side, så den ligner
  // resten af KitMaker: Mine godkendelsesmails – altså i UX og design»).
  // On: "My approval e-mails", the organisation's approval e-mail settings,
  // the sent-mail log, the e-mail overview and its Change panel use the
  // FieldGroup / bordered-row-list / SecondaryButton / PrimaryButton /
  // InputLabel idiom of the other My profile sections (Notifications,
  // Company details, Users). Pure presentation: the same view-models, the
  // same radios/checkboxes/fields with the same test ids, the same texts,
  // 0 new calls. Default (unset/"false") = today's markup, byte-identical.
  // CI builds don't read .env.dev - set it in the workflow yml (own PR).
  profileApprovalMailUxEnabled:
    process.env['REACT_APP_PROFILE_APPROVAL_MAIL_UX_ENABLED'] === 'true',
  // Feature flag: the design list's Status cell follows the approval
  // (Nichlas 17/10: «statusser kommer ud som godkendt selvom den står i
  // korrektur i nav – den burde vel komme som afventer»). On: a design whose
  // approval group has a participant (vendor / customer / end customer) with
  // status Pending shows "AWAITING · Vendor / Customer / End Customer" in
  // the list's Status cell (and in the design popup, which shows the status
  // text for status approval only) - whether design.status is done (JS
  // upload creates done + vendor Pending; list cell only) or approval
  // (deferred customer approval) - exactly the rows the orderability guard
  // already refuses to order. "APPROVED" / "APPROVED BY …" is shown only when no
  // participant is pending; rejected / draft rows are untouched. Pure
  // display over fields the list rows already carry - 0 new calls. Default
  // (unset/"false") = today's texts, byte-identical. CI builds don't read
  // .env.dev - set it in the workflow yml (own PR).
  designStatusFollowsApprovalEnabled:
    process.env['REACT_APP_DESIGN_STATUS_FOLLOWS_APPROVAL_ENABLED'] === 'true',
  // Feature flag: "Approve without ordering" for a design WITHOUT a customer
  // (Nichlas 17/10: «jeg har som forhandler rettet og godkendt den … kan
  // ikke lægge den i kurv»; and: «det må de faktisk stadig: Send til
  // kunde»). On: the dealer's approval dialog (design editor "Approve",
  // list/popup approval start) offers three choices when the design has no
  // customer (no customer org, or the customer is a vendor-type org = empty
  // Customer column): "Send Approval to Customer" (today's external
  // approval - the dealer types the e-mail address), "Add to order" (today's)
  // and a clear "Approve without ordering" button (= approval-start
  // skip=true, the design is done and orderable at once) in place of the
  // italic "Continue without ordering" link, with a line explaining the
  // choice. Without the skip button a design sent to nobody hangs in
  // approval with only Delete.
  // Paired with the API flag DesignApprovalStartNoCustomerAutoSkipEnabled -
  // either alone is safe, but the FE rule is WIDER: the API flag only
  // treats CustomerId = null as "no customer", while the FE also counts
  // CustomerId = VendorId (NAV import, empty Customer column), so the API
  // alone does not cover the NAV-imported rows. Logos and the customer/admin dialogs are
  // untouched. 0 new calls. Default (unset/"false") = today's dialog,
  // byte-identical. CI builds don't read .env.dev - set it in the workflow
  // yml (own PR).
  editorNoCustomerSkipEnabled:
    process.env['REACT_APP_EDITOR_NO_CUSTOMER_SKIP_ENABLED'] === 'true',
  // Feature flag: the customer's public approval page (public/design/
  // approval/:id?code=…, also /approval?type=design&keys=…) draws the design
  // with PublicDesignPreview instead of the shared StyleViewer/DesignImage
  // (Nichlas 18/10: «det større er ikke stort, front og ryg skifter står
  // også forkert, man kan heller ikke se logoerne»). On: one big <img> with a
  // definite height for the selected view (the design's own Front/Back blobs,
  // which DO carry the logos), views ordered Front/Back/Left/Right like the
  // thumbnail sheet and opened on Front, thumbnail buttons whose selection
  // mark follows the clicked view by NAME (the API sends no id, so today every
  // thumbnail looks selected), a small "Logos on: Back" hint while a view
  // without logos is shown, fallback view -> displayImage (lowRes under,
  // highRes fading in, like the dealer's popup) -> style thumbnail ->
  // placeholder, "already approved by your dealer" instead of Approve/Reject
  // on a decided design, and a readable toast when the verdict call fails.
  // Pure display over the fields the public GET already returns - 0 new
  // calls, API untouched. Default (unset/"false") = today's page,
  // byte-identical (StyleViewer/DesignImage untouched). CI builds don't read
  // .env.dev - set it in the workflow yml (own PR).
  publicApprovalPreviewV2Enabled:
    process.env['REACT_APP_PUBLIC_APPROVAL_PREVIEW_V2_ENABLED'] === 'true',
  // Feature flag: SEND DESIGN in the dealer's popup while the dealer's OWN
  // vote is still pending (Nichlas 18/10: «åbner man pop up og vil sende til
  // kunde … skal man godkende den … eller skal den godkende vendor og så til
  // gul/orange ved kunde? så vil det give mening»). Today that click first
  // shows "Approval required - approve now?", and send then fires TWO calls
  // (PUT approval + POST approval-send, which places no customer vote at all,
  // so the Customer row never turns yellow). On: the confirmation dialog is
  // skipped for the dealer, the send dialog opens at once with one hint line
  // ("Your approval is recorded when you send"), and send = ONE call,
  // approval-start {skip:false, email} - the API places the dealer's vote
  // itself (Vendor Approved = green, Customer Pending = yellow) and the
  // repository lands the answer in list and popup as every approval does.
  // -1 call. Customer, admin, a dealer whose vote is already placed, the logo
  // popup and the editor/list approval dialogs are untouched. Default
  // (unset/"false") = today's dialog and today's two calls, byte-identical.
  // CI builds don't read .env.dev - set it in the workflow yml (own PR).
  sendToCustomerImplicitVendorApproveEnabled:
    process.env['REACT_APP_SEND_TO_CUSTOMER_IMPLICIT_VENDOR_APPROVE_ENABLED'] === 'true',
  // Feature flag: a working Edit in the design list's "See note" dialog
  // (Nichlas 18/10, prod, row MUL352935 «Test nm» AFVIST: «når jeg trykker
  // regidere her på en der har været afvist kommer jeg ikke ind og kan lave
  // rettelsen»). Today the screen renders RejectionNoteDialog without onEdit
  // and without a logo, so the dialog's logo gate shows an Edit button whose
  // onClick is undefined - a dead button. On: the dialog closes and the
  // editor opens through handleGoToUpdateDesign - the very handler the
  // popup's Edit and the row's Edit already use (same navigation, 0 new
  // calls) - and only when the popup would show Edit for that design
  // (dealer/admin, Rejected, a style, isEditable not false, no pending vote
  // of the user's own); otherwise the dialog hides Edit (showEdit=false).
  // The customer never sees it. The logo list's and the order list's use of
  // the dialog are untouched. Default (unset/"false") = today's props and
  // today's dead button, byte-identical. CI builds don't read .env.dev - set
  // it in the workflow yml (own PR).
  designRejectionNoteEditEnabled:
    process.env['REACT_APP_DESIGN_REJECTION_NOTE_EDIT_ENABLED'] === 'true',
  // Feature flag: "Email settings" for dealers (FORSLAG-VENDOR-EMAIL-STYRING
  // 16/10 + Nichlas' decisions 16/10 21:4x). On: My profile (Vendor, or an
  // admin impersonating a vendor org) gets a section "Email settings" with
  // one row per mail type (order confirmation, new order to you, order
  // needs your approval, approval requests, shipped/ready for pickup -
  // NEVER the Stripe payment mails) where the dealer's primary contact /
  // an admin picks who receives each mail (placer, order e-mail, customer
  // main e-mail, dealer main e-mail, up to 5 extra addresses) as To or CC;
  // other dealer users see it read-only, customers never see it. ONE GET
  // organizations/{orgId}/email-settings the first time the section is
  // opened, ONE PUT per Save - 0 calls on any other page. PAIRED with the
  // API app setting VendorEmailSettingsEnabled (API first: the routes
  // answer 404 while it is off = the section says "not active yet").
  // Default (unset/"false") = no nav item, no DOM, no calls = byte-
  // identical. CI builds don't read .env.dev - set it in the workflow yml
  // (own PR).
  vendorEmailSettingsEnabled:
    process.env['REACT_APP_VENDOR_EMAIL_SETTINGS_ENABLED'] === 'true',
  // Feature flag: every dealer user may change "Also to me" in My profile's
  // e-mail overview (Nichlas 19/10: «den enkelte bruger skal også kunne
  // rette det på sig selv – admin/primær sætter det overordnede, og
  // derefter kan brugeren selv rette»). Today the overview shows Change
  // only to the org's primary contact / an admin (the API's canEdit), so a
  // colleague can neither open the "Designs & logos ready for approval"
  // panel nor change their own choice (follow the organisation / always /
  // never + reminders) - the choice the "Approval e-mails" section let
  // them change before the overview. On: that ONE row shows Change to every
  // user (while the personal view-model is there); in its panel the
  // recipients ("Who should receive this e-mail?", "Other addresses", "Use
  // the standard again") and "For the whole organisation" are read-only for
  // a non-primary user (the existing read-only sentences), "Also to me" is
  // editable, and Save hands back ONLY the personal draft = ONE PUT
  // profile/approval-mail-preferences (the caller's own row, allowed for
  // every vendor user) - never setRule = never PUT organizations/{orgId}/
  // email-settings (403 for a non-primary user), never the org's
  // approval-mail PUT. The primary contact / an admin and every other row
  // are untouched. 0 new calls. Default (unset/"false") = today's overview,
  // byte-identical. CI builds don't read .env.dev - set it in the workflow
  // yml (own PR).
  emailSettingsPersonalEditEnabled:
    process.env['REACT_APP_EMAIL_SETTINGS_PERSONAL_EDIT_ENABLED'] === 'true',
  // Flag REACT_APP_PASSWORD_SET_LINK_ENABLED (19/10, Nichlas: «når vi er i
  // admin går en på en forhandler kan nulstille deres kode og sende et
  // link som vi kopier ind i en mail og sender til dem»): (1) the admin
  // action "Reset code & create link" per user on the org edit screen's
  // "Users & contact" section - ONE POST ops/organizations/{orgId}/users/
  // {userId}/password-link per click; the API cancels the user's earlier
  // links, stores only the hash of a new 48-hour token and answers
  // { link, expiresUtc }; the dialog shows the link ONCE with Copy, and the
  // admin pastes it into their own e-mail (KitMaker mails nothing). The
  // user's current password keeps working until the link is used, unless
  // the admin ticks "block the current password now". (2) The public page
  // /public/set-password#t=<token> (the token in the FRAGMENT, never a
  // query parameter) where the user chooses their own password: ONE
  // token-free POST public/password-link { code, password } on Save, 0
  // calls before. The button needs the profile role Admin AND the account
  // on REACT_APP_PASSWORD_SET_LINK_ADMIN_EMAILS (below); the API is the
  // authority anyway (flag PasswordSetLinkEnabled + its own allow-list,
  // fail-closed). Default (unset/"false") = no route, no button =
  // byte-identical DOM and wire. CI builds don't read .env.dev - set it in
  // the workflow yml (own PR).
  passwordSetLinkEnabled:
    process.env['REACT_APP_PASSWORD_SET_LINK_ENABLED'] === 'true',
  // The admins who see "Reset code & create link": comma-separated e-mails,
  // read like apiKeysAdminEmails (trimmed, lower-cased, matched against the
  // MSAL account's e-mails). EMPTY = nobody (fail-closed), the same shape
  // as the API's PasswordSetLinkAdminEmails, which decides; this list only
  // hides the button.
  passwordSetLinkAdminEmails: (
    process.env['REACT_APP_PASSWORD_SET_LINK_ADMIN_EMAILS'] ?? ''
  )
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean),
  // Flag REACT_APP_LOGO_IMAGE_VERSION_AFTER_SAVE_ENABLED (19/10, logo 731314:
  // the list kept the OLD thumbnail after «fix it myself» + colour change
  // until a hard reload): the thumbnail URL's ?v= token comes from
  // colorChange.startedAtUtc, which the API sets ONCE at the start of the
  // editor's save chain, while ChangeLogoColor, ResizeImageLogo and
  // UpdateImageLogo each rewrite the thumbnail blob under the same name
  // afterwards - the browser cached the first (old) bytes under the new
  // token. When "true": the repository records the time of each of those
  // three answers per logo (utils/functions/logoImageVersion, per tab) and
  // the mapper takes max(API token, that time), so the list rows and the
  // popup mapped after the last write carry a fresh token and fetch the
  // finished blob. 0 extra API calls (the stale-flag refetch is today's).
  // Default (unset/"false") = today's token, byte-identical. CI builds
  // don't read .env.dev - set it in the workflow yml (own PR).
  logoImageVersionAfterSaveEnabled:
    process.env['REACT_APP_LOGO_IMAGE_VERSION_AFTER_SAVE_ENABLED'] === 'true',
  // Flag REACT_APP_CART_PERSIST_SLIM_ENABLED (20/10, cart persist slim): the
  // cart's localStorage bucket was 45,2 M chars for 3 design lines in prod
  // (act-as Sport 24 Business Silkeborg): the API rides its `organizations`
  // list onto the design (80 orgs, 6,3 M chars) and onto every logo copy
  // (86 orgs, 6,5 M chars each) through the mapper spread, and today's v1
  // writer keeps them - Chrome's 5-10 MB quota throws QuotaExceeded and the
  // cart is empty after a refresh (the 30/9 reports). When "true": right
  // before the v1 writer (slimItemsForStorage), the v2 writer
  // (serializeCartForStorage) and the fallback shadow, every line loses the
  // design's and the logos' `organizations` lists (utils/cartPersistSlim;
  // no cart / checkout / order reader touches them), nothing else; the
  // in-memory items keep everything. A slimmed bucket reads back through
  // the same parse / rehydrate as today. 0 HTTP calls. Default
  // (unset/"false") = byte-identical payload. CI builds don't read .env.dev
  // - set it in the workflow yml (own PR).
  cartPersistSlimEnabled:
    process.env['REACT_APP_CART_PERSIST_SLIM_ENABLED'] === 'true',
  // Flag REACT_APP_LOGO_STATUS_BADGE_WRAP_ENABLED (20/10, Nichlas' screenshot
  // of the Danish logo list, admin as dealer org 979): under GODKENDT the
  // status line "Sendes til leverandøren" ran into the "SE DESIGNS" button
  // of the actions column. The status column is 1/12 of the row (~112 px at
  // the page's 90 rem cap), the status-line badges are whitespace-nowrap in
  // a cell with no overflow clip, and the da/no text measures 148.5 px in
  // Inter 600 12 px (en 124.3 px, "Upload mislykkedes" 128.8 px, "Logo-
  // forberedelse forsinket" 175.2 px - the siblings overflow too). When
  // "true": the three status-line badges (NavWriteBackPendingBadge,
  // UploadFailedBadge, ProductPendingBadge) in the list row's and the
  // popup's status cell wrap inside the cell (whitespace-normal, break-words,
  // leading-tight, max-w-full; the dot stays on the first line) so the
  // whole text stays readable; the price-column badge and every other call
  // site keep today's classes. 0 calls. Default (unset/"false") = today's
  // class strings, byte-identical DOM. CI builds don't read .env.dev - set
  // it in the workflow yml (own PR).
  logoStatusBadgeWrapEnabled:
    process.env['REACT_APP_LOGO_STATUS_BADGE_WRAP_ENABLED'] === 'true',
  // Flag REACT_APP_LOGO_EDITOR_ALL_CURRENT_COLORS_ENABLED (20/10, item
  // 1103001 «logo-hotscreen», Transfer standard, colour PMS 3546 C): the
  // logo editor's "Current Colors" was EMPTY and the palette / "Change
  // Color" stayed locked behind the overlay. The colour changer looks the
  // logo's current colours up ONLY in options.colors (the API's
  // GetAvailableColors(onlyIsChangeColor) = the supplier's change colours),
  // so a logo whose colour the supplier does not mark IsChangeColor gets no
  // row, nothing can be selected and the palette never unlocks. When
  // "true": every code in logo.colors gets its row - the method's list
  // first (as today), then options.allColors, then the logo's own
  // colorDetails (presentation/screens/logo-editor/functions/
  // currentColorLookup). The palette on the right (what one can switch TO)
  // and the ColorChange {from, to} contract are unchanged. 0 new API
  // calls. Default (unset/"false") = today's rows, byte-identical DOM. CI
  // builds don't read .env.dev - set it in the workflow yml (own PR).
  logoEditorAllCurrentColorsEnabled:
    process.env['REACT_APP_LOGO_EDITOR_ALL_CURRENT_COLORS_ENABLED'] === 'true',
  // Flag REACT_APP_LOGO_CORRECTION_APPROVAL_DIALOG_ENABLED (release 38,
  // 21/10, Nichlas: «det tager for lang tid i forhold til kunderne»): the
  // dealer who fixed a NAV logo themselves ("Fix it myself", flags
  // REACT_APP_LOGO_REJECT_AND_EDIT_NAV_ENABLED + _BLOCK_CUSTOMER_SEND_
  // ENABLED) today meets a disabled "Send Approval to Customer", a help
  // line and an italic skip link; after the click the toast says "Approval
  // skipped", the badge's tooltip "This usually takes a minute" and "Add to
  // order" a red "1 logos skipped" - while prod telemetry shows the
  // supplier system (app117) takes the change in minutes to ~10 hours.
  // When "true": (1) ONLY on top of the live block (customerSendBlocked,
  // never for noCustomer) the editor's approval dialog shows ONE primary
  // action, "Approve my correction" (= onSkip, the way to the supplier
  // system) plus "Add to order", with an honest line before and after.
  // (2)-(4) follow the API's PENDING answer for any "Fix it myself" NAV
  // logo (isRejectAndEditNavLogo + navWriteBackPending; the block flag is
  // not read), so the list's dialog and the popup's skip button get them
  // too: (2) the success toast of the skip approval reads "Correction
  // approved - it is being sent ... Reload the page to see it." (10 s);
  // (3) "Add to order" on a pending correction shows a calm info toast
  // instead of the red error; (4) the pending badge's tooltip says
  // ordering opens when the supplier system has taken the change. 0 new
  // calls. Default (unset/"false") = today's dialog, toasts and tooltip,
  // byte-identical DOM. CI builds don't read .env.dev - set it in the
  // workflow yml (own PR).
  logoCorrectionApprovalDialogEnabled:
    process.env['REACT_APP_LOGO_CORRECTION_APPROVAL_DIALOG_ENABLED'] === 'true',
  // Flag REACT_APP_ADMIN_USER_SEARCH_ENABLED (Nichlas 24/10-2026: «søge en
  // brugernavn via dens email, navn, telefonnummer, samt se hvornår de har
  // været logget ind … deaktivere og slette … det må ikke gøre systemet
  // langsomt og man skal kunne søge hurtigt»): the admin page "Users" - the
  // SAME route admin/vendor-users as today's "Vendor users" page (the route
  // exists without a flag; only its menu item is gated), relabelled
  // headerFooter.adminUserSearch and shown with this flag OR
  // billingOverviewEnabled. On: the search runs as you type (ONE call 300 ms
  // after the last key, never under 2 characters; Enter / Search still apply
  // at once; a cleared field = today's unfiltered list), and every row
  // carries its organisation (chip Vendor / Customer + a link to its "Users
  // & contact" page; a customer org names its dealer), the last login
  // (lastLoginUtc in the browser's local time; "logged in, date not
  // recorded" for HasLoggedIn from before login tracking; "never logged
  // in"), a status chip (active / deactivated with its date as visible text)
  // and the actions Deactivate / Reactivate (ONLY together with
  // userDeactivateEnabled above, whose 403 logout, retry rule and act-as
  // filter make a deactivated user safe - without it the page offers delete
  // only; the same repository call, confirm and texts as "Users & contact":
  // ONE PATCH organizations/{orgId}/users/{userId}/status; 409
  // selfDeactivate / lastActiveUser as the translated toasts) and Delete
  // (the same deleteUser call and confirm: ONE DELETE users/{id}; 409
  // selfDelete / lastUser as translated toasts), each followed by ONE
  // refetch of the list; a row's buttons are disabled while its action
  // runs. PAIRED with the API flag VendorUsersSearchV2Enabled, which adds
  // orgType / parentVendorOrgId / parentVendorName / lastLoginUtc /
  // hasLoggedIn / isDeactivated / deactivatedUtc to GET users/vendor-users
  // (same COUNT + one paged SELECT) and widens the search to phone and
  // customer orgs; an answer without those fields shows a dash and treats
  // the row as a vendor user (never a crash), so either flag may go first.
  // Calls: page open = ONE GET exactly as today (0 new calls); re-entry
  // inside the app's cacheTime = ONE GET (refetchOnMount "always" on this
  // one query - the global refetchOnMount:false would show a 5-min-old list
  // with 0 calls, and the page acts on what it shows); ONE GET per debounced
  // term (never per key); the last login and the status ride on the row (0
  // extra calls); ONE PATCH / DELETE + ONE refetch of the list per confirmed
  // action, plus at most ONE GET users?orgId when that org's "Users &
  // contact" list is still cached from a visit in the same session (the
  // repository's existing refetch keeps it fresh); a refused action (409 /
  // 403 / 502) = no refetch, an ambiguous failure (timeout, 5xx) = ONE.
  // Prod order: API UserDeactivateEnabled -> FE userDeactivateEnabled -> API
  // VendorUsersSearchV2Enabled -> this flag. Default (unset/"false") =
  // byte-identical DOM, calls and route table (pinned by a snapshot of the
  // untouched screen). CI builds don't read .env.dev - set it in the
  // workflow yml.
  adminUserSearchEnabled:
    process.env['REACT_APP_ADMIN_USER_SEARCH_ENABLED'] === 'true',
}
